import { PLAYTEST } from '../shared/playtest.ts';
import { openFeedback } from './feedback.ts';
import { TIER_IDS, TOUR_TIERS, nextStartFrom } from '../shared/tournament.ts';
import { peekClick, renderPeek, bindPeek, mountPeekLinks } from './peek.ts';
import { toggleCorner } from './corner/index.ts';
import { hubHead, hubPane, hubClick, bindHub, setShowFilter } from './player-hub.ts';
import { bindAccountSettings } from './account-settings.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES, LINES, BOSSES, FINAL_TOTAL, DEX_SPECIES } from '../shared/species.ts';
import { ratingOf } from '../shared/rating.ts';
import { BOSS_RELIC_CHANCE } from '../shared/bosses.ts';
import { FAVOURED_FROM, OUTMATCHED_BELOW } from '../shared/boss-estimate.ts';
import { BEATS, ELEMENT_IDS, HUNT_STRONG, HUNT_WEAK, PVP_STRONG, PVP_WEAK } from '../shared/elements.ts';
import { CRIT } from '../shared/combat.ts';
import { SLOT_POWER, SLOT_LEVEL, WAVE_CD_POWER, WAVE_MIN_TARGETS, WAVE_REACH_TEXT, WAVE_SPLASH } from '../shared/moves.ts';
import { TABLET_COINS } from '../shared/tablets.ts';
import { GROUNDS, GROUND, AREAS, AREA, REGIONS } from '../shared/grounds.ts';
import { WEEK_CATCH } from '../shared/weekcatch.ts';
import { PAID_BOOST, PAID_BOOST_KINDS } from '../shared/boosts.ts';
import { EVENTS, EVENT_KINDS, eventLabel, type ServerEvent } from '../shared/events.ts';
import { eventsHelp, serverEvents, utcWhen } from './events.ts';
import { ITEM, ITEMS, REVIVE, REVIVE_HP, shopPriceAt, STONE_NAME, STAR_ITEM, BOSS_TOKEN, MAX_STARS, starItemFor, relicName, relicGrade } from '../shared/items.ts';
import { firstKeep } from '../shared/restock.ts';
import { REFINE_BASE, refineTotalCost } from '../shared/refine.ts';
import { SHRINE_TIERS, SHRINES_PER_FUSE, SHRINES_IN_USE, SHARDS_PER_SHRINE, CHARM_SHARE, shrineChance } from '../shared/shrines.ts';
import { RECIPES, CREST_COINS, type Recipe } from '../shared/recipes.ts';
import { EGGS, EGG_UNLOCK_LEVEL, INCUBATOR, HATCH_LEVEL, eggBloodlineOdds, eggAtLeast } from '../shared/eggs.ts';
import { QUEST_COIN_KILLS, QUEST_SEALS, QUEST_POTIONS, QUEST_BONUS_DIAMONDS, QUEST_WEEKLY_COIN_KILLS, QUEST_WEEKLY_SEALS, QUEST_WEEKLY_POTIONS, QUEST_CHALLENGE_BOOST_MIN, MARKS_DAILY, MARKS_WEEKLY, CHEST, CHEST_MAX_MARKS, QUEST_STAGES, WEEKLY_NEED, HOURLY_FROM, HOURLY_DEFEAT, HOURLY_CATCH, HOURLY_COIN_KILLS, HOURLY_SEALS, HOURLY_POTIONS, rewardLabel } from '../shared/quests.ts';
import { TRAITS } from '../shared/traits.ts';
import { trainerXpToNext, creatureXpToNext, killXp, huntKillCoins, levelUpCoins, totalXp, LEVEL_SLACK, WIPE_XP_LOSS } from '../shared/xp.ts';
import { catchChance, RARITY_PRICE } from '../shared/capture.ts';
import type { CreatureDTO, ClientMsg, Broadcast, ChatChannel } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { chatShown } from './chatfilter.ts';
import { spriteUrl, iconUrl, uiIcon, has, creatureIcon } from './assets.ts';
import { unread, isChannel, channelName } from './hud.ts';
import { CHAT_CHANNELS } from '../shared/protocol.ts';
import { renderTeam, renderHuntCard, renderAuto, renderRailTabs, setRailTab, feedChannel, huntRegion, toggleHuntEmotes, markSeen, renderSession, resetSession, toggleSession, toggleLog, setLogTab, setCapSort, sessionXpPerHour, mountTeamDrag, mountUseDrag, toggleAutoSection, dismissSuggestion, showSuggestion, leadCreature, useListChange } from './hud.ts';
import { setZoom, getZoom, batterySaver, setBatterySaver, fieldWatching } from './field.ts';
import { drawGolden } from './golden.ts';
import { mountManualCatch } from './catch.ts';
import { mountWayfarers, playHuntEmote } from './wayfarers.ts';
import { calcPane, calcInput, calcAct, loadCalc, calcSpecies } from './calc.ts';
import { speciesSheet } from './species-sheet.ts';
import { esc, num, full, elChip, cname, left, duration, estimate } from './fmt.ts';
import { patch, whenReleased } from './dom.ts';
import { renderStrip } from './strip.ts';
import { mountHoverCard, refreshHoverCard, closeHoverCard } from './hovercard.ts';
import { mountTips } from './tip.ts';
import { route, go, depth, TAB_ROUTE } from './router.ts';
import { ICON } from './icons.ts';
import { auraOneInNow } from './auracharm.ts';
import { OUTFITS, OUTFIT_PRICE, RANGER_LEVEL, ownsOutfit, outfitHow } from '../shared/outfits.ts';
import { bindConfirm, confirmClick, CONFIRM_MONEY_MS } from './confirm.ts';
import { bindOutfits, trainerOutfits, shownOutfit, outfitsClick, clearPreview} from './outfits.ts';
import { DEX_MILESTONES, dexProgress, milestoneNeed } from '../shared/bestiary.ts';
import { ALTAR, ALTAR_GIFT } from '../shared/altar.ts';
import { ELEMENTS } from '../shared/elements.ts';
import type { RegionId } from '../shared/species.ts';
import { creaturePage, pickRefineStat, pickRefineItem } from './creature-page.ts';
import { pickTrainStat, pickTrainStone } from './stonetrain.ts';
import { ascendDialog } from './ascend.ts';
import { awakenConfirm } from './awakening.ts';
import { AWAKENINGS, AURA_SHARD_SHARE, SHARDS_PER_STONE, SHARD_CHANCE_CAP, awakenedId, bossShardChance, ladderShardChance } from '../shared/awakening.ts';
import { AWAKENED_CD_MS } from '../shared/moves.ts';
import { nickInput, nickSubmit, nickClear } from './nametags.ts';
import { storeAdminSection } from './store.ts';
import { mapPane, mapClick, mapChange } from './map.ts';
import { shopPane, shopHead, bagPane, marketPane, bossPane, pagesClick, pagesChange, bindPages, pxSlot, buyListing } from './pages.ts';
import { MARKET_MIN_UNIT, MARKET_MAX_OPEN, MARKET_RELIST_MS, listingFee, marketFee } from '../shared/market.ts';
import { openDialog, dialogOpen, toast } from './dialog.ts';
import { creaturesPane, teamPane, creaturesHead, creaturesClick, creaturesOpen, creaturesChange, creaturesKey, depotList, bindCreatures, pruneSelection, creaturesFit, showCaughtSince } from './creatures.ts';
import { renderAway, closeAway, awaySince, noteRegion } from './away.ts';
import { marketInput, marketClick, marketHead, openMarketRoute, marketFit } from './market-view.ts';
import { curIcon, curWord, GEM_ICON } from './money.ts';
import { GEM_LIST_LEVEL, GEM_MIN_RATING, GEM_MIN_UNIT, cashoutUsd, creatureGemRatingOk, gemFee, gemFeePct, gemListable, gemUsd, relicGemListable } from '../shared/gems.ts';
import type { MarketCurrency } from '../shared/market-query.ts';
import { sampleAdminRow } from './sample-ui.ts';
import { dexAllOn, dexAllPane, dexAllSwitch, setDexAll } from './dex-all.ts';
import { communityHead, recordsPane, boardsPane, communityClick, communityChange, loadCommunity, recordLine, bindCommunity } from './community.ts';
import { statusText, retryIn } from './status.ts';
import { creatureGrade, auraTag } from './badges.ts';
import { miniPills } from './rollpills.ts';
import { SHOWOFF } from '../shared/showcase.ts';
import { BOARDS, PODIUM, monthTitle } from '../shared/leaderboards.ts';
import { arenaPane, arenaHead, arenaClick, arenaHelp, loadArena, bindArena } from './arena.ts';
import { boardViewClick } from './boardview.ts';
import { hallsPane, hallsHead, hallsClick, hallsHelp, loadHalls, bindHalls } from './halls.ts';
import { worldBossPane, worldBossHead, worldBossClick, loadWorldBoss, bindWorldBoss } from './worldboss.ts';
import { synergyHelp } from './synergy.ts';
import { boardHelp } from './rows.ts';
import { questsPane, renderQuestSummary, questsWaiting } from './quests.ts';
import { renderEggSummary, renderGlance } from './eggsum.ts';
import { renderStarter } from './starter.ts';
import { starterOpen, starterReady } from '../shared/starter.ts';
import { renderStarterPick } from './starterpick.ts';
import { mountTutorial, renderTutorial, replayTutorial } from './tutorial.ts';
import { UNLOCK, unlocked } from '../shared/unlocks.ts';
import { RANKED, THEMES } from '../shared/ranked.ts';
import { guildPane, guildHead, guildClick, guildInput, guildSubmit, loadGuild, bindGuild, tagChip, guildWarHelp } from './guild.ts';
import { mountProfileCard } from './profilecard.ts';
import { playerIcon } from './playericon.ts';
import { friendsHead, friendsPane, playerHead, playerPane, socialClick, socialInput, socialSubmit, socialHelp, openSocialRoute, bindSocial, chatHidden, playerLink, socialWaiting } from './social.ts';
import { trainingClick, trainingInput, bindTraining } from './training.ts';
import { planPane, planHead, planClick, planInput, planHelp, loadPlan, bindPlan } from './plan.ts';
import { passPane, passHead, passClick, passHelp, chatBadge, bindPass } from './pass.ts';
import { nameColorOf, passClaimable, passSeason, SEASONS } from '../shared/pass.ts';
import { FOUNDER } from '../shared/founder.ts';
import { tournamentsPane, tournamentsHead, tournamentsClick, tournamentsInput, tournamentsHelp, loadTournaments, bindTournaments } from './tournaments.ts';
import { BANK_GOAL, GUILD_BOOST_MULT, BOOST_MS, GUILD_COST, ROSTER_SIZE, LEAVE_COOLDOWN_MS, POSTER_MAX, DONATE_SHORTCUTS } from '../shared/guilds.ts';
export { toast };
import { bindStreams, streamsClick } from './streams.ts';
import { bindExchange, exchangeAdminSection, exchangeClick } from './exchange.ts';
import { bindPlayerReport, playerReportClick, playerReportSection } from './player-report.ts';
import { bindStoreGems, gemPackClick, headerGems, packsOn, payByClick } from './store.ts';
import { diamondAdminSection } from './diamond-report.ts';
import { creatureArt, skinsAdmin } from './skins.ts';
import type { StreamsAdmin } from '../shared/streams.ts';

let send: (m: ClientMsg) => void = () => {};
export function bindSend(fn: (m: ClientMsg) => void) {
  send = fn;
  bindPages({ send, render: () => render(true) });
  bindCommunity({ send, render: () => render(true) });
  bindPeek(send);
  bindArena({ send, render: () => render(true) });
  bindHalls({ send, render: () => render(true) });
  bindHeld({ send, render: () => render(true) });
  bindTrail({ send, render: () => render(true) });
  bindOutfits({ send, render: () => render(true) });
  bindConfirm(() => render(true));
  bindGuild({ send, render: () => render(true) });
  bindSocial({ send, render: () => render(true) });
  bindMessages({ send, render: () => render(true) });
  bindTraining({ send, render: () => render(true) });
  bindPlan({ send, render: () => render(true) });
  bindTournaments({ send, render: () => render(true) });
  bindWorldBoss({ send, render: () => render(true) });
  bindPass({ send });
  bindStreams({ send, render: () => render(true) });
  bindExchange({ send });
  bindPlayerReport({ send, render: () => render(true) });
  bindStoreGems({ send });
  bindCreatures({ send, render: () => render(true), list: (id) => void listCreature(id) });
}

import { helpPane, helpInput } from './help.ts';
import { psecClick, openSection, psecTabs, psecWrap } from './psec.ts';
import { fitLists, bindFitLists, fitFocus } from './fitlist.ts';
import { loudOn, setLoud, loudButton, loudPlaceholder, loudCommand } from './loudspeaker.ts';
import { LOUD } from '../shared/loudspeaker.ts';
import { bindMessages, messagesClick, messagesInput, chatCommand, dmWaiting, reportsAdminSection, chatSel, selectChannel, selectDm, selectList, chatRouteSel, listHtml, convLinesHtml, convHeadHtml, sendDm, isBlocked, loadInbox, dmFromSelect, M as DM } from './messages.ts';
import { closeProfileCard } from './profilecard.ts';
import { mountKinhall, kinhallPresence, renderKinhall, HALL_ICON } from './kinhall.ts';
import { bindHeld, heldClick, heldChange, heldHelp, heldLabel } from './held.ts';
import { bindTrail, trailPane, trailClick, trailHead, trailHelp, openTrailRoute, bossesRedirect } from './trail.ts';
import { HELD_ITEMS, heldName, heldPiece, pieceChance } from '../shared/held.ts';

const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;

// ---------------- shell ----------------
// App bar on top; below it either the hunt or a full-screen page with a live
// hunt strip. No sheet ever covers the field.
export function mountGame(root: HTMLElement) {
  root.innerHTML = `
  <div class="app">
    <header class="appbar" id="top"></header>
    <div class="strip" id="strip" hidden></div>
    <div class="game" id="huntview" data-ptab="team">
      <nav class="ptabs" id="ptabs" aria-label="Hunt panels"></nav>
      <aside class="lcol">
        <section class="panel playercard" id="playercard" aria-label="Your trainer"></section>
        <section class="panel team scroll" id="teamlist"></section>
        <section class="panel startersum" id="startersum" hidden aria-label="First steps"></section>
        <section class="panel shrinesum" id="shrinesum"></section>
        <section class="panel boostsum" id="boostsum" aria-label="Active boosts"></section>
        <section class="panel questsum" id="questsum" hidden></section>
        <section class="panel questsum eggsum" id="eggsum" hidden aria-label="Incubators"></section>
        <section class="panel glancesum" id="glancesum" hidden aria-label="At a glance"></section>
      </aside>
      <main class="stage">
        <canvas id="field"></canvas>
        <section class="saver" id="saver" hidden aria-label="Battery saver"></section>
        <div class="ov huntcard" id="fieldhead"></div>
        <div class="ov suggest" id="suggest" hidden></div>
        <div class="ov stopped" id="stopped" hidden></div>
        <div class="ov away" id="away" hidden></div>
        <div class="travel" id="travel" hidden aria-live="polite"></div>
        <div class="ov combatlog" id="combatlog" aria-live="polite"></div>
        <div class="session" id="session"></div>
        <div class="fieldtools" id="fieldtools" role="toolbar" aria-label="Scene"></div>
      </main>
      <aside class="rcol" id="rcol" data-rtab="auto">
        <nav class="rtabs" id="rtabs" aria-label="Right panel"></nav>
        <section class="panel auto scroll" id="auto"></section>
        <section class="panel feed grow">
          <nav class="tabs2" id="feedtabs" aria-label="Feed"></nav>
          <div class="list" id="feed"></div>
          <form id="chat"><input maxlength="200" placeholder="Message World" aria-label="Chat message"><button class="btn">Send</button></form>
        </section>
      </aside>
    </div>
    <main class="kinhall" id="kinhall" hidden></main>
    <main class="page" id="page" hidden><div class="page-inner"><div class="pagehead" id="pagehead"></div><div class="pane" id="pane"></div></div></main>
    <nav class="bnav" id="bnav" aria-label="Pages"></nav>
  </div>
  <div class="toasts" id="toasts"></div>`;
  $('#chat').addEventListener('submit', (e) => { e.preventDefault(); chatSubmit($('#chat input') as HTMLInputElement); });
  document.addEventListener('click', onClick);
  new ResizeObserver(queueNavFit).observe($('#top'));
  mountManualCatch($('#field') as HTMLCanvasElement, (m) => send(m));
  mountTutorial((m) => send(m));
  mountKinhall($('#kinhall'), (m) => send(m));
  mountWayfarers((m) => send(m), fieldWatching);
  mountHoverCard();
  mountTips();
  mountProfileCard();
  mountPeekLinks();
  bindHub({ rate: trainerRate });
  bindAccountSettings({ rerender: () => render(true) });
  mountTeamDrag((ids) => send({ t: 'team', ids }));
  bindFitLists(() => render(true));
  mountUseDrag((kind, rows) => send({ t: 'settings', settings: { [kind]: rows } }));
  document.addEventListener('keydown', onKey);
  document.addEventListener('change', onChange);
  document.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.dataset?.adm && el.type !== 'checkbox') admForm[el.dataset.adm] = el.value;
    if (el.dataset?.calc && calcInput(el)) render(true);
    if (el.dataset?.depotFilter !== undefined) { S.depotFilter = el.value; render(true); }
    if (el.dataset?.phFilter !== undefined) { setShowFilter(el.value); render(true); }
    if (el.dataset?.rec === 'q' && communityChange(el)) render(true);
    if ((el.dataset?.msearch !== undefined || el.dataset?.mgem !== undefined) && marketInput(el)) render(true);
    if (el.dataset?.g) { const r = guildInput(el); if (r === 'render') render(true); }
    socialInput(el); trainingInput(el); messagesInput(el);
    if (nickInput(el)) render(true);
    if (helpInput(el)) render(true);
  });
  document.addEventListener('submit', (e) => { if (guildSubmit(e.target as HTMLFormElement) || socialSubmit(e.target as HTMLFormElement) || cpSubmit(e.target as HTMLFormElement) || nickSubmit(e.target as HTMLFormElement, send)) e.preventDefault(); });
  window.addEventListener('hashchange', applyRoute);
  applyRoute();
}

// ---------------- routes ----------------
const NAV: [string, string, string][] = [
  ['kinhall', 'Kinhall', HALL_ICON], ['hunt', 'Hunt', ICON.hunt], ['map', 'Map', ICON.map], ['creatures', 'Creatures', ICON.creatures], ['bag', 'Bag', ICON.bag], ['shop', 'Shop', ICON.shop], ['pass', 'Pass', ICON.trophy],
  ['market', 'Market', ICON.market], ['chat', 'Chat', ICON.mail], ['bosses', 'Bosses', ICON.bosses], ['arena', 'Arena', ICON.arena], ['halls', 'Halls', ICON.halls], ['plan', 'Battle Plan', ICON.plan], ['tournaments', 'Tournaments', ICON.cup], ['worldboss', 'World Boss', ICON.worldboss], ['bestiary', 'Bestiary', ICON.book], ['leaderboards', 'Ranks', ICON.trophy], ['guild', 'Guild', ICON.guild], ['friends', 'Friends', ICON.friends], ['help', 'Help', ICON.help],
];
/** The app bar's pages: with the Trail on (shared/trail.ts) it takes the Bosses link's place. */
const navList = (): [string, string, string][] => (S.trailOn ? NAV.map((n): [string, string, string] => (n[0] === 'bosses' ? ['trail', 'Trail', ICON.bosses] : n)) : NAV);
/** A nav link's icon: the rig-drawn one where it exists (art/rig3d/icons/ui-*.ts), else the line SVG. */
const NAV_ART: Record<string, string> = {
  hunt: 'ui-hunt', map: 'ui-map', creatures: 'ui-creatures', bag: 'ui-bag', shop: 'ui-shop', market: 'ui-market', bosses: 'ui-bosses', worldboss: 'ui-bosses', trail: 'ui-trail', arena: 'ui-arena', halls: 'ui-halls',
  plan: 'ui-plan', tournaments: 'ui-tournaments', bestiary: 'ui-bestiary', leaderboards: 'ui-ranks', guild: 'ui-guild', friends: 'ui-friends', chat: 'ui-chat', help: 'ui-info',
};
/** Painted art when it exists, else the line SVG; both sit in the same 24 px box, so art can drop in without a shift. */
// The season pass uses the running season's pass icon (art/icon-pass-<season>), Frostfall's until a season has its own.
const passIcon = () => { const own = `pass-${passSeason(Date.now()).name.toLowerCase().replace(/\s+/g, '')}`; return has(`icon-${own}`) ? own : 'pass-frostfall'; };
const navIcon = (id: string, svg: string) => (id === 'pass' ? `<img class="ui-ic nav-pass" src="${iconUrl(passIcon())}" width="24" height="24" alt="">` : NAV_ART[id] ? uiIcon(NAV_ART[id], svg, 'ui-ic', 24) : svg);
/**
 * How many open links the app bar shows; the rest fold into More. Measured to the bar's width (fitNav), so a
 * wide screen shows every link and a laptop folds only what doesn't fit. Phones use the bottom bar instead.
 */
let navFit = 99;
let navFitQueued = false;
let lastTop = '';
function queueNavFit() {
  if (navFitQueued) return;
  navFitQueued = true;
  requestAnimationFrame(() => { navFitQueued = false; fitNav(); });
}
function fitNav() {
  const top = document.getElementById('top'), nav = top?.querySelector<HTMLElement>('.nav');
  if (!top || !nav || !nav.getClientRects().length) return;
  const links = [...nav.querySelectorAll<HTMLElement>(':scope > a[data-nav]')];
  const more = nav.querySelector<HTMLElement>('.navmore');
  // Measure with every link and More showing and the trainer pill at its natural width.
  top.classList.add('nav-measure');
  const widths = links.map((a) => a.getBoundingClientRect().width);
  const moreW = more?.getBoundingClientRect().width ?? 0;
  const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
  const cs = getComputedStyle(top), tgap = parseFloat(cs.columnGap) || 0;
  let avail = top.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  const brand = top.querySelector<HTMLElement>('.brand');
  // 761-1200 px the nav has a row of its own; otherwise it shares the bar with everything else.
  const ownRow = !!brand && nav.offsetTop > brand.offsetTop + 8;
  if (!ownRow) {
    for (const el of top.children) {
      if (el === nav || el.classList.contains('spacer') || !el.getClientRects().length) continue;
      avail -= el.getBoundingClientRect().width + tgap;
    }
    avail -= tgap + 12;
  }
  top.classList.remove('nav-measure');
  const locked = nav.dataset.locked === '1';
  const total = widths.reduce((a, w) => a + w, 0) + gap * Math.max(0, widths.length - 1);
  let k = widths.length;
  if (total + (locked ? gap + moreW : 0) > avail) {
    let used = 0;
    for (k = 0; k < widths.length; k++) {
      const next = used + widths[k] + (k ? gap : 0);
      if (next + gap + moreW > avail) break;
      used = next;
    }
  }
  links.forEach((a, i) => a.classList.toggle('ovf', i >= k));
  more?.classList.toggle('gone', k >= links.length && !locked);
  if (k !== navFit) { navFit = k; lastTop = ''; renderTop(); }
}
const CHEVRON = '<svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
const TITLES: Record<string, string> = {
  kinhall: 'Kinhall', map: 'Map', creatures: 'Creatures', creature: 'Creature', bag: 'Bag', shop: 'Shop', market: 'Market', bosses: 'Bosses', trail: 'The Trail',
  bestiary: 'Bestiary', help: 'Help', calculator: 'Grade calculator', admin: 'Admin', account: 'Trainer', trainer: 'Trainer',
  records: 'Ranks', leaderboards: 'Ranks', quests: 'Quests', guild: 'Guild', arena: 'Arena', halls: 'Element Halls', plan: 'Battle Plan', tournaments: 'Tournaments', worldboss: 'World Boss', friends: 'Friends', chat: 'Chat', player: 'Trainer profile', pass: 'Season pass',
};
/** Scroll position per page, so Close returns to the same spot in a list. */
const scrollAt = new Map<string, number>();
let shownKey = '';
/**
 * Phones scroll the window, not #page, and it kept its place from page to page: a hunt picked low on the Map opened
 * the Hunt page scrolled past its scene, and the Bag opened at its bottom (2026-10-25). Each page now opens where it
 * was left, or at the top; the Hunt always on its scene.
 */
const windowAt = new Map<string, number>();
let windowKey = '';
function windowView(key: string) {
  if (key === windowKey) return;
  windowKey = key;
  const y = key === 'hunt' ? 0 : windowAt.get(key) ?? 0;
  if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
}
addEventListener('scroll', () => { if (windowKey) windowAt.set(windowKey, window.scrollY); }, { passive: true });
/** Set by the / key: focus the Creatures search once that page has rendered. */
let focusFilter = false;
/** An element id to bring into view once the page has rendered (#/creatures/team/shrines). */
let scrollTo = '';
let menu: '' | 'account' | 'gift' | 'more' | 'nav' = '';
/** Phone only: which panel shows under the lead card on the hunt. */
const PTABS = [['team', 'Team'], ['trainer', 'Trainer'], ['log', 'Log'], ['auto', 'Auto'], ['chat', 'Chat']] as const;
let ptab: string = (() => { try { const v = localStorage.getItem('mi-ptab') ?? 'team'; return v === 'world' ? 'chat' : v; } catch { return 'team'; } })();
function renderPhoneTabs() {
  const hv = $('#huntview');
  if (hv.dataset.ptab !== ptab) hv.dataset.ptab = ptab;
  // A reward waiting under Trainer (quests or first steps) marks its tab, so a player on Team still sees it.
  const t = S.trainer;
  const waiting = !!t && (questsWaiting().count > 0 || (starterOpen(t) && starterReady(t).length > 0));
  patch($('#ptabs'), PTABS.map(([id, n]) => `<button class="${ptab === id ? 'on' : ''}" data-act="ptab" data-id="${id}" aria-pressed="${ptab === id}">${id === 'trainer' && waiting && ptab !== id ? `<span class="pt-l">${n}<i class="dot" title="Rewards to claim"></i></span>` : n}</button>`).join(''));
}
/**
 * Computers and tablets: the Trainer tab adds the weekly quests and the incubators while the column has room.
 * When it doesn't (boosts, a shrine and first steps all showing on a short screen) the at-a-glance card goes, then the incubators, then the weekly rows.
 */
function fitTrainer() {
  const col = document.querySelector<HTMLElement>('#huntview > .lcol');
  if (!col) return;
  const ALL = ['fit-noglance', 'fit-noegg', 'fit-nowk', 'fit-tight', 'fit-nochest', 'fit-nochips'];
  col.classList.remove(...ALL);
  if (ptab !== 'trainer' || S.tab) return;
  if (innerWidth <= 760) {
    // Phones: the quest card takes what is left under the trainer card. Tighter rows first, then the chest
    // line (it is on the Quests page), then the boost and shrine chips, until the last quest row shows.
    const q = document.getElementById('questsum');
    if (!q || q.hidden) return;
    const qOver = () => q.scrollHeight > q.clientHeight + 1;
    for (const c of ['fit-tight', 'fit-nochest', 'fit-nochips']) if (qOver()) col.classList.add(c);
    return;
  }
  const over = () => col.scrollHeight > col.clientHeight + 1;
  for (const c of ['fit-noglance', 'fit-noegg', 'fit-nowk']) if (over()) col.classList.add(c);
}
let creatureId: number | null = null;

function applyRoute() {
  const r = route();
  if (location.hash.match(/^#c\d+$/)) { go(`#/creature/${r.sub}`, true); return; }
  S.page = r.page;
  menu = '';
  closeHoverCard();
  switch (r.page) {
    case 'hunt': S.tab = ''; break;
    case 'creatures':
      S.tab = r.sub === 'team' ? 'team' : 'depot';
      if (r.sub2 === 'shrines') scrollTo = 'shrines';
      S.depotView = r.sub === 'favorites' ? 'collection' : r.sub === 'for-sale' ? 'listed' : 'all';
      break;
    case 'creature': S.tab = 'creature'; creatureId = Number(r.sub); break;
    case 'map':
      S.tab = 'map'; selectedArea = AREA[r.sub2] ? r.sub2 : null;
      S.mapRegion = REGIONS.some((x) => x.id === r.sub) ? r.sub : GROUND[S.trainer?.ground ?? '']?.region ?? 'midgard';
      markAreasSeen();
      break;
    case 'bestiary': S.tab = 'dex'; S.dexSpecies = SPECIES[r.sub] ? r.sub : ''; break;
    // Help's currency topic reads the gem wallet, which only the Market loads: ask once, without joining its updates.
    case 'help': S.tab = 'wiki'; if (!S.market) send({ t: 'market' }); break;
    case 'calculator': S.tab = 'calc'; break;
    case 'market':
      S.tab = 'market';
      // #/market/:id, from a Trade card: show that listing.
      openMarketRoute(r.sub, r.sub2);
      if (S.marketFocus) scrollTo = 'mfocus';
      break;
    case 'admin': S.tab = 'admin'; send({ t: 'admin' }); break;
    // #/shop/diamonds/skins, from the Bag's Skins pocket: the Diamond shop at its Skins section.
    case 'shop': S.tab = 'shop'; if (r.sub2 === 'skins') openSection('dshop', 'skins'); else if (r.sub2 === 'vip') { openSection('dshop', 'top'); fitFocus('ds-feat', '#shop-vip'); } break;
    case 'records': case 'leaderboards': S.tab = r.page; loadCommunity(r.page, r.sub); break;
    case 'guild': S.tab = 'guild'; loadGuild(); break;
    case 'arena': S.tab = 'arena'; loadArena(); break;
    case 'halls': S.tab = 'halls'; loadHalls(); break;
    case 'worldboss': S.tab = 'worldboss'; loadWorldBoss(); break;
    case 'plan': S.tab = 'plan'; loadPlan(); break;
    case 'tournaments': S.tab = 'tournaments'; loadTournaments(); break;
    // The Trail replaces the Bosses page while it is on: #/bosses leads to the right boss node.
    case 'bosses': if (S.trailOn && S.trainer) { go(bossesRedirect(), true); return; } S.tab = 'bosses'; break;
    case 'trail': if (!S.trailOn) { go('#/bosses', true); return; } S.tab = 'trail'; openTrailRoute(r.sub, r.sub2); break;
    case 'friends': case 'player': S.tab = r.page; openSocialRoute(); break;
    // Messages moved into chat: old links land on the Chat page.
    case 'messages': go(r.sub === 'to' && r.sub2 ? `#/chat/dm/${encodeURIComponent(r.sub2)}` : r.sub === 'blocked' ? '#/friends/blocked' : r.sub === 'requests' ? '#/chat/requests' : '#/chat/inbox', true); return;
    case 'chat': S.tab = 'chat'; openChatRoute(); break;
    // The player screen shows friends and the guild: fetch both fresh.
    case 'account': case 'trainer': S.tab = r.page; send({ t: 'social' }); if (S.trainer?.guild) send({ t: 'guild' }); break;
    default: S.tab = r.page;
  }
  render(true);
}

/** Opens a page on request (a toast's View button); never called on its own. */
export function openTab(tab: string) { go(TAB_ROUTE[tab] ?? '#/hunt'); }

function isAdminHint() { return localStorage.getItem('mi-admin') === '1'; }

/** Map shows a dot when an area has opened since you last looked at the map. */
function openAreas() { const lv = S.trainer?.level ?? 0; return AREAS.filter((a) => lv >= a.level).length; }
function markAreasSeen() { try { localStorage.setItem('mi-areas-seen', String(openAreas())); } catch { /* storage off */ } }
function newAreaOpen() { try { const seen = Number(localStorage.getItem('mi-areas-seen') ?? '0'); if (!seen) { markAreasSeen(); return false; } return openAreas() > seen; } catch { return false; } }

// ---------------- render ----------------
let fitDepth = 0;
export function render(force = false) {
  const t = S.trainer;
  if (!t) return;
  if (whenReleased(() => render(force))) return;
  const onHunt = !S.tab;
  if (S.page !== 'trainer') clearPreview();
  renderTop();
  $('#huntview').hidden = !onHunt;
  const stage = $('.stage');
  if (stage.classList.contains('saver-on') !== batterySaver()) stage.classList.toggle('saver-on', batterySaver());
  // Kinhall is its own scene, outside the page frame; the live hunt strip still shows above it.
  const inHall = S.tab === 'kinhall';
  $('#page').hidden = onHunt || inHall;
  $('#strip').hidden = onHunt;
  kinhallPresence(inHall);
  renderHuntCard();
  renderTeam();
  renderQuestSummary();
  renderEggSummary();
  renderGlance();
  renderStarter();
  renderStarterPick(send);
  renderTutorial();
  renderAuto();
  renderRailTabs();
  // Conversations and lists change without a broadcast: redraw them with the page.
  if (chatSel().k !== 'ch') drawChat($('#feed'), $('#chat') as HTMLFormElement, false);
  renderPhoneTabs();
  fitTrainer();
  noteRegion();
  renderAway();
  renderSession();
  // A Golden Wild stands on the field while the hunt shows, and floats over any other page.
  drawGolden();
  pruneSelection();
  refreshHoverCard();
  renderPeek();
  if (onHunt) { shownKey = ''; windowView('hunt'); return; }
  renderStrip();
  if (inHall) { shownKey = ''; renderKinhall(); windowView('kinhall'); return; }
  const key = `${S.tab}:${S.depotView}:${S.dexSpecies}:${S.tab === 'creature' ? creatureId : S.tab === 'bag' || S.tab === 'wiki' ? route().sub : ''}`;
  const page = $('#page');
  if (key !== shownKey && shownKey) scrollAt.set(shownKey, page.scrollTop);
  // Pages narrower than the frame get a narrower frame (public/style.css), so title, tabs and content centre together.
  if (page.dataset.view !== S.page) page.dataset.view = S.page;
  patch($('#pagehead'), pageHead());
  // Keyed by page, so switching pages starts fresh while updates within one only patch it.
  patch($('#pane'), `<div data-key="${esc(key)}">${paneHtml()}</div>`);
  fitLists($('#page'));
  // One re-render for a new page size, never a chain of them: a fit that keeps changing would freeze the tab.
  if (S.page === 'creatures' && creaturesFit() && fitDepth < 2) { fitDepth++; try { return render(true); } finally { fitDepth--; } }
  if (S.page === 'market') marketFit();
  if (S.page === 'chat') stickFeed($('#cpfeed'));
  windowView(key);
  if (key !== shownKey) { page.scrollTop = scrollAt.get(key) ?? 0; shownKey = key; }
  if (key !== shownKey || scrollTo) {
    const id = scrollTo; scrollTo = '';
    const el = id ? document.getElementById(id) : null;
    if (el) el.scrollIntoView({ block: id === 'mfocus' ? 'center' : 'start' });
    // The market list arrives after the page opens: try again on the next render.
    else if (id === 'mfocus' && S.page === 'market') scrollTo = id;
  }
  if (focusFilter && S.page === 'creatures') { focusFilter = false; ($('[data-depot-filter]') as HTMLInputElement | null)?.focus(); }
}

function renderTop() {
  const t = S.trainer!;
  const need = trainerXpToNext(t.level);
  const now = serverNow();
  const pct = Math.floor((t.xp / need) * 100);
  const rate = sessionXpPerHour();
  const today = new Date().toISOString().slice(0, 10), yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  const ready = t.daily.last !== today;
  const qw = questsWaiting();
  const gift = ready || qw.count > 0;
  const nextDay = ready ? (t.daily.last === yesterday ? t.daily.streak + 1 : 1) : t.daily.streak;
  const days = CONFIG.dailyClaimDiamonds;
  const page = S.page || 'hunt';
  const hasKey = BOSSES.some((b) => (t.bag[BOSS_TOKEN[b.id]] ?? 0) > 0);
  const navOn = (id: string) => id === page || (id === 'creatures' && page === 'creature') || (id === 'leaderboards' && page === 'records') || (id === 'friends' && page === 'player');
  const social = socialWaiting();
  const dm = dmWaiting();
  // Progressive disclosure (shared/unlocks.ts): a locked page leaves the bar and shows as a quiet "Lv N" line in the menus.
  // Ranked seasons (shared/ranked.ts) open the Arena and the Battle Plan at Lv 40 while they are on.
  const rankedOpen = (id: string) => S.ranked && (id === 'arena' || id === 'plan') && t.level >= RANKED.unlockLevel;
  const open = (id: string) => unlocked(t, id, now) || navOn(id) || rankedOpen(id);
  const lockLv = (id: string) => (S.ranked && (id === 'arena' || id === 'plan') ? RANKED.unlockLevel : UNLOCK[id].level);
  const lockedLine = (id: string, name: string) => `<span class="locked" title="${esc(name)} opens at trainer level ${lockLv(id)}">${esc(name)} <span class="muted">Lv ${lockLv(id)}</span></span>`;
  const nav = navList();
  const mainNav = nav.filter(([id]) => open(id));
  const folded = mainNav.slice(navFit), lockedNav = nav.filter(([id]) => !open(id));
  const moreOn = folded.some(([id]) => navOn(id));
  // A page with something waiting gets a dot, in the bar or in the More menu; More carries one for its folded pages.
  const navDot = (id: string) => ((id === 'bosses' || id === 'trail') && hasKey) || (id === 'map' && newAreaOpen()) || (id === 'friends' && social)
    ? `<i class="dot" title="${id === 'bosses' || id === 'trail' ? 'You hold a boss key' : id === 'friends' ? 'Requests, messages or invites waiting' : 'A new area is open'}"></i>` : '';
  // Unread messages ride on the Chat link (and on the phone's chat button).
  const chatMark = dm.unread ? `<span class="badge">${dm.unread > 99 ? '99+' : dm.unread}</span>` : dm.requests ? '<i class="dot" title="Message requests"></i>' : '';
  const gems = headerGems();
  const html = `
    <a class="brand" href="#/hunt">Legendkin</a>${(t.speed ?? 1) > 1 ? `<a class="speedchip" href="#/admin" title="Playtest speed ×${t.speed}: this account runs ${t.speed} times faster. Turn it off on the Admin page." aria-label="Playtest speed ×${t.speed}">×${full(t.speed!)}</a>` : ''}
    <nav class="nav" aria-label="Pages" data-locked="${lockedNav.length ? 1 : 0}">${mainNav.map(([id, name, icon], i) => `<a href="#/${id}" data-nav="${id}" class="${navOn(id) ? 'on' : ''}${i >= navFit ? ' ovf' : ''}"${navOn(id) ? ' aria-current="page"' : ''}${id === 'chat' ? ` title="Chat" aria-label="Chat${dm.unread ? `, ${dm.unread} unread` : ''}"` : ''}>${navIcon(id, icon)}<span>${name}</span>${id === 'chat' ? chatMark : ''}${navDot(id)}</a>`).join('')}
      <span class="menuwrap navmore${folded.length || lockedNav.length ? '' : ' gone'}"><button class="${moreOn ? 'on' : ''}" data-act="menu-nav" aria-haspopup="true" aria-expanded="${menu === 'nav'}">More${folded.some(([id]) => (id === 'chat' && chatMark) || navDot(id)) ? '<i class="dot"></i>' : ''}${CHEVRON}</button>
        ${menu === 'nav' ? `<div class="menu">${folded.map(([id, name, icon]) => `<a href="#/${id}"${navOn(id) ? ' class="on" aria-current="page"' : ''}>${navIcon(id, icon)}<span>${name}</span>${id === 'chat' ? chatMark : ''}${navDot(id)}</a>`).join('')}${lockedNav.map(([id, name]) => (UNLOCK[id] ? lockedLine(id, name) : '')).join('')}</div>` : ''}</span></nav>
    <span class="spacer"></span>
    <a class="trainer" href="#/trainer" aria-label="Trainer page"><b style="color:${nameColorOf(t, now) ?? 'inherit'}">${esc(t.name)}</b><span class="lvl">Lv ${t.level}</span><span class="mini-xp"><i style="width:${pct}%"></i><span>${pct}%</span></span>
      <span class="lvring" style="--p:${pct}" aria-hidden="true"><span>${t.level}</span></span><span class="pop">Trainer XP ${full(t.xp)} / ${full(need)}${rate ? ` · next level in ${duration(((need - t.xp) / rate) * 3_600_000)} at ${num(rate)} XP/h` : ''}</span></a>
    <span class="cur"><img src="${iconUrl('coin')}" alt="Coins"><span class="long">${full(t.coins)}</span><span class="short">${num(t.coins)}</span></span>
    <a class="cur" href="#/shop/${packsOn() ? 'packs' : 'diamonds'}" title="Diamonds" aria-label="${full(t.diamonds)} diamonds"><img src="${iconUrl('diamond')}" alt="">${full(t.diamonds)}</a>
    ${gems !== null ? `<a class="cur gemcur" href="#/market/gems" title="Gems" aria-label="${full(gems)} gems"><img src="${GEM_ICON}" alt=""><span class="long">${full(gems)}</span><span class="short">${num(gems)}</span></a>` : ''}
    <a class="iconbtn phone-only${S.page === 'chat' ? ' on' : ''}" href="#/chat" aria-label="Chat${dm.unread ? `, ${dm.unread} unread` : ''}" title="Chat">${uiIcon('ui-chat', ICON.mail, 'ui-ic', 24)}${chatMark}</a>
    <span class="menuwrap"><button class="iconbtn" data-act="menu-gift" aria-label="Daily reward and quests" aria-expanded="${menu === 'gift'}">${ICON.gift}${gift ? '<i class="dot"></i>' : ''}</button>
      ${menu === 'gift' ? `<div class="menu"><b>Daily reward</b><p>Day ${((nextDay - 1) % days.length) + 1} of ${days.length} · ${days[(nextDay - 1) % days.length] ? `${days[(nextDay - 1) % days.length]} diamond${days[(nextDay - 1) % days.length] === 1 ? '' : 's'} and ` : ''}10 Rune Seals</p>
        ${ready ? '<button class="btn primary" data-act="daily">Claim</button>' : `<p class="sub">Claimed today · next in ${duration(Date.parse(`${today}T00:00:00Z`) + 86_400_000 - Date.now())}</p>`}
        <hr><b>Quests</b><p>${qw.count ? `Ready to claim: ${full(qw.coins)} coins${Object.keys(qw.items).length ? ', supplies' : ''}${qw.diamonds ? ` and ${qw.diamonds} diamonds` : ''}.` : `${(t.quests?.today.list ?? []).filter((x) => x.progress >= x.need).length} of 3 done today, ${(t.quests?.week?.list ?? []).filter((x) => x.progress >= x.need).length} of 3 this week.`}</p>
        ${qw.count ? '<button class="btn gold" data-act="quest-claim">Claim quests</button> ' : ''}<a class="link" href="#/quests">See quests</a></div>` : ''}</span>
    ${S.playersOnline ? `<span class="players">${full(S.playersOnline)} online</span>` : ''}
    <span id="netstatus" title="Connection to the server" class="net ${S.online ? 'on' : retryIn() > 0 ? 'wait' : 'off'}">${statusText()}</span>
    <span class="menuwrap"><button class="avatar" data-act="menu-account" aria-label="Account menu" aria-expanded="${menu === 'account'}">${esc(t.name.slice(0, 1).toUpperCase())}</button>
      ${menu === 'account' ? `<div class="menu"><a href="#/trainer">Trainer</a>${isAdminHint() ? '<a href="#/admin">Admin</a>' : ''}<button data-act="logout">Log out</button></div>` : ''}</span>
    <span class="menuwrap phone-only"><button class="iconbtn morebtn" data-act="menu-more" aria-label="Menu" aria-expanded="${menu === 'more'}">${ICON.menu}${gift || hasKey || social ? '<i class="dot"></i>' : ''}</button>
      ${menu === 'more' ? `<div class="menu">${[['quests', 'Quests'], ['pass', 'Season pass'], ['shop', 'Shop'], ['market', 'Market'], S.trailOn ? ['trail', 'Trail'] : ['bosses', 'Bosses'], ['arena', 'Arena'], ['halls', 'Halls'], ['plan', 'Battle Plan'], ['tournaments', 'Tournaments'], ['worldboss', 'World Boss'], ['bestiary', 'Bestiary'], ['leaderboards', 'Ranks'], ['guild', 'Guild'], ['friends', 'Friends'], ['help', 'Help'], ['trainer', 'Trainer']].sort(([a], [b]) => Number(!open(a)) - Number(!open(b))).map(([id, n]) => !open(id) ? lockedLine(id, n) : `<a href="#/${id}">${n}${((id === 'bosses' || id === 'trail') && hasKey) || (id === 'quests' && qw.count) || (id === 'pass' && passClaimable(t, serverNow())) || (id === 'friends' && social) ? ' <i class="dot"></i>' : ''}</a>`).join('')}${isAdminHint() ? '<a href="#/admin">Admin</a>' : ''}
        ${ready ? '<button data-act="daily">Claim daily reward</button>' : '<p class="sub">Daily claimed</p>'}<button data-act="logout">Log out</button></div>` : ''}</span>`;
  patch($('#top'), html);
  // Anything in the bar changed width (a page opened, the wallet grew a digit): fit the links again.
  if (html !== lastTop) { lastTop = html; queueNavFit(); }
  const BNAV: [string, string, string][] = [['kinhall', 'Kinhall', HALL_ICON], ['hunt', 'Hunt', ICON.hunt], ['map', 'Map', ICON.map], ['creatures', 'Creatures', ICON.creatures], ['bag', 'Bag', ICON.bag]];
  const more = !BNAV.some(([id]) => navOn(id));
  patch($('#bnav'), `${BNAV.map(([id, name, icon]) => `<a href="#/${id}" class="${navOn(id) ? 'on' : ''}">${icon}<span>${name}</span>${id === 'map' && newAreaOpen() ? '<i class="dot"></i>' : ''}</a>`).join('')}
    <button class="${more || menu === 'more' ? 'on' : ''}" data-act="menu-more" aria-expanded="${menu === 'more'}">${ICON.menu}<span>More</span>${gift || hasKey || social ? '<i class="dot"></i>' : ''}</button>`);
}

function pageHead(): string {
  const p = S.page;
  // The Bag draws its own header card (client/bagshop.ts).
  if (p === 'creature' || p === 'map' || p === 'bag' || p === 'help') return '';
  let meta = '', tabs = '', actions = '';
  if (p === 'creatures') ({ meta, tabs, actions } = creaturesHead());
  if (p === 'shop') ({ tabs, actions } = shopHead());
  if (p === 'market') ({ meta, tabs, actions } = marketHead());
  if (p === 'records' || p === 'leaderboards') ({ meta, tabs, actions } = communityHead());
  if (p === 'guild') ({ meta, actions } = guildHead());
  if (p === 'arena') ({ meta, actions } = arenaHead());
  if (p === 'halls') ({ meta, actions } = hallsHead());
  if (p === 'worldboss') ({ meta, actions } = worldBossHead());
  if (p === 'plan') ({ meta, actions } = planHead());
  if (p === 'tournaments') ({ meta, actions } = tournamentsHead());
  if (p === 'trail') ({ meta, actions } = trailHead());
  if (p === 'pass') ({ meta, actions } = passHead());
  if (p === 'friends') ({ meta, tabs, actions } = friendsHead());
  if (p === 'trainer' || p === 'account') ({ meta, tabs } = hubHead());
  if (p === 'player') ({ meta, actions } = playerHead());
  return `<div class="pagetitle"><h1>${TITLES[p] ?? ''}</h1>${meta ? `<span class="meta">${meta}</span>` : ''}<span class="spacer"></span>${actions}</div>${tabs}`;
}

function paneHtml(): string {
  switch (S.tab) {
    case 'team': return teamPane();
    case 'depot': return creaturesPane();
    case 'creature': return creaturePage(creatureId ?? -1);
    case 'map': return mapPane(selectedArea);
    case 'bag': return bagPane();
    case 'shop': return shopPane();
    case 'market': return marketPane();
    case 'bosses': return bossPane();
    case 'trail': return trailPane();
    case 'dex': return dexPane();
    case 'quests': return questsPane();
    case 'arena': return arenaPane();
    case 'halls': return hallsPane();
    case 'worldboss': return worldBossPane();
    case 'plan': return planPane();
    case 'tournaments': return tournamentsPane();
    case 'pass': return passPane();
    case 'wiki': return helpPane();
    case 'calc': return calcPane();
    case 'admin': return adminPane();
    case 'account': case 'trainer': return hubPane(isAdminHint());
    case 'records': return recordsPane();
    case 'leaderboards': return boardsPane();
    case 'guild': return guildPane();
    case 'friends': return friendsPane();
    case 'chat': return chatPane();
    case 'player': return playerPane();
  }
  return '';
}

// ---------------- keys ----------------
function onKey(e: KeyboardEvent) {
  // Dialogs and the hover card take Esc first (capture listeners); this is what's left.
  if (e.key === 'Escape') {
    if (menu) { menu = ''; render(true); return; }
    if (S.page === 'creature') { closeCreature(); return; }
    if (S.tab) go('#/hunt');
    return;
  }
  const el = e.target as HTMLElement;
  if (!dialogOpen() && !el.closest?.('input, select, textarea, [contenteditable]') && creaturesKey(e)) return;
  if (e.ctrlKey || e.metaKey || e.altKey || dialogOpen() || el.closest?.('input, select, textarea, [contenteditable]')) return;
  const k = e.key.toLowerCase();
  if (k === 'h') go('#/hunt');
  else if (k === 'm') go('#/map');
  else if (k === 'p') void toggleCorner();
  else if (k === 'c') go('#/creatures');
  else if (k === 'b') go('#/bag');
  else if (e.key === '/') {
    e.preventDefault();
    if (S.page === 'creatures') ($('[data-depot-filter]') as HTMLInputElement | null)?.focus();
    else { focusFilter = true; go('#/creatures'); }
  } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && S.page === 'creature' && creatureId !== null) {
    const list = depotList();
    const i = list.findIndex((c) => c.id === creatureId);
    const next = list[i + (e.key === 'ArrowLeft' ? -1 : 1)];
    if (i >= 0 && next) go(`#/creature/${next.id}`, true);
  }
}
let selectedArea: string | null = null;


function dexPane() {
  if (S.dexSpecies && SPECIES[S.dexSpecies]) return speciesSheet(S.dexSpecies);
  if (dexAllOn()) return dexAllPane();
  const t = S.trainer!;
  const caught = Object.values(t.dex).filter((d) => d.caught > 0).length;
  // One region at a time under tabs; a region's cards page to fit the screen (client/fitlist.ts).
  const keys = REGIONS.map((r) => r.id);
  const tabs = psecTabs('dex', REGIONS.map((r) => ({ key: r.id, title: r.name, dot: dexProgress(t.dex, t.dexClaimed, r.id).claimable > 0 })), undefined, true);
  return `<div class="dex-head"><span class="dex-count"><b>${caught}</b> of ${DEX_SPECIES.length} species caught</span>${dexAllSwitch()}</div>
    ${tabs}
    ${REGIONS.map((r) => psecWrap('dex', r.id, keys, `${dexMilestones(r.id, r.mythology)}
      <div class="grid dex-grid" data-fit="dex-${r.id}">${LINES.filter((l) => l.region === r.id).flatMap((l) => l.stages).map((s) => {
        const d = t.dex[s.id];
        const known = (d?.seen ?? 0) > 0 || (d?.bought ?? 0) > 0;
        return `<div class="card"${known ? ` data-act="dex-open" data-id="${s.id}"` : ''} data-key="dx-${s.id}" title="${known ? esc(s.name) : 'Unknown'}"><img src="${spriteUrl(s.id)}" alt="" style="${known ? '' : 'filter:brightness(0) opacity(.5)'}">
          <div class="nm">${known ? esc(s.name) : '???'}</div><div class="lv">${d?.caught ? `caught ${d.caught}` : d?.bought ? 'bought' : known ? 'seen' : ''}</div></div>`;
      }).join('')}</div>`, true)).join('')}`;
}

/** A region's catch progress and its milestone rewards, each claimable once. */
function dexMilestones(region: RegionId, myth = '') {
  const t = S.trainer!;
  const pr = dexProgress(t.dex, t.dexClaimed, region);
  const steps = DEX_MILESTONES.map((m, i) => {
    const need = milestoneNeed(m, pr.total);
    const state = i < pr.claimed ? 'done' : i < pr.reached ? 'ready' : 'todo';
    return `<li class="dm-${state}"><b>${m.pct}%</b><span>${need} species</span><span class="dm-r"><img src="${iconUrl('diamond')}" alt="">${m.diamonds}</span><span class="dm-s">${state === 'done' ? '✓ Claimed' : state === 'ready' ? 'Ready' : `${Math.max(0, need - pr.caught)} to go`}</span></li>`;
  }).join('');
  return `<div class="dexm"><div class="dexm-top"><span>${myth ? `<span class="muted">${myth} · </span>` : ''}<b>${pr.caught}</b> of ${pr.total} caught</span><span class="bar dexm-bar"><i style="width:${(pr.caught / pr.total) * 100}%"></i></span>
      ${pr.claimable ? `<button class="btn gold small" data-act="dex-claim" data-id="${region}">Claim ${pr.claimable} diamonds</button>` : ''}</div>
    <ol class="dexm-steps">${steps}</ol></div>`;
}

// Admin form values survive the pane being rebuilt after each command.
const admForm: Record<string, string> = {
  level: '50', coins: '100000', diamonds: '0', item: ITEMS[0].id, itemQty: '10',
  species: Object.keys(SPECIES)[0], cLevel: '20', bloodline: '0', aura: '', talent: 'random',
  egg: EGGS[0].id, eggRegion: 'midgard',
  evKind: 'xp', evMult: '2', evStart: '', evHours: '2', evRegion: '',
  teamLevel: '20', ground: GROUNDS[0].id, ffMinutes: '60', speed: '50', outfit: OUTFITS.find((o) => o.source.kind === 'shop')!.id,
  rankedTheme: '',
  heldItem: HELD_ITEMS[0].id, heldQ: '75', heldEl: ELEMENT_IDS[0] as string, heldDel: '', pieceDel: '', pieceQty: '10',
};
const admVal = (k: string) => esc(admForm[k] ?? '');

/** Admin cash-outs with a Pay sent and no answer yet (cleared when the Admin page comes back). */
export const gemPaying = new Set<number>();

/** Gems (shared/gems.ts): the switch, totals, a test credit, and the cash-out queue to pay through Stripe or refuse. */
function gemsAdmin() {
  const g = S.admin?.gems;
  if (!g) return '';
  const usdC = (c: number) => `$${(c / 100).toFixed(2)}`;
  const q = g.queue.map((w) => {
    const busy = gemPaying.has(w.id);
    const note = [w.disputed ? '<b class="bad">Disputed card</b>' : '', w.hold && !w.disputed ? `<span class="bad">${esc(w.hold)}</span>` : '', w.err ? `<span class="muted">Last try: ${esc(w.err)}</span>` : ''].filter(Boolean).join('<br>');
    return `<tr><td>${esc(w.who ?? '')}${note ? `<br>${note}` : ''}</td><td class="r">${full(w.gems)}</td><td class="r">${usdC(w.cents)}</td><td>${new Date(w.created).toLocaleString()}</td><td><button class="btn small" data-act="adm-gem-pay" data-id="${w.id}"${g.stripe && !w.disputed && !busy ? '' : ' disabled'}${w.disputed ? ' title="Frozen while the card dispute is open"' : ''}>${w.disputed ? 'Frozen' : busy ? 'Paying…' : `Pay ${usdC(w.cents)}`}</button><button class="btn small" data-act="adm-gem-refuse" data-id="${w.id}"${busy ? ' disabled' : ''}>Refuse</button></td></tr>`;
  }).join('');
  return `<h4 class="adm-h">Gems</h4>
    <div class="actions"><span class="muted">${g.on ? 'Open: listings can be priced in gems; players see their wallet.' : 'Closed: no one sees gems, and gem listings are hidden. Balances are kept.'}</span><button class="btn small" data-act="adm-gems" data-id="${g.on ? '0' : '1'}">${g.on ? 'Close gems' : 'Open gems'}</button></div>
    <div class="actions"><span class="muted">Players hold <b>${full(g.held)}</b> (${gemUsd(g.held)}) · our fees <b>${full(g.fees)}</b> (${gemUsd(g.fees)}) · diamond packs <b>${full(g.shop)}</b> · earned ${full(g.earned)} · bought ${full(g.bought)} · paid out ${usdC(g.paidOut)} · waiting ${full(g.queued)} in ${g.queuedN}${g.stripe ? '' : ' · Stripe off: cash-outs cannot be paid'}</span></div>
    <div class="actions"><span class="muted">${g.cashoutOn ? 'Cash-out open: players can cash out earned gems.' : 'Cash-out closed: gems can be bought and traded, not cashed out.'}</span><button class="btn small" data-act="adm-gem-cashout-on" data-id="${g.cashoutOn ? '0' : '1'}">${g.cashoutOn ? 'Close cash-out' : 'Open cash-out'}</button></div>
    <div class="actions"><input data-adm="gemName" value="${admVal('gemName')}" placeholder="Trainer name" aria-label="Trainer name" style="width:140px">${admNum('gemAmt', 'Gems (− takes)', 120).replace('min="0" ', '')}<button class="btn small" data-act="adm-gem-credit">Credit gems (playtest accounts only)</button></div>
    ${q ? `<table class="tbl"><tr><th>Trainer</th><th class="r">Gems</th><th class="r">Pays</th><th>Asked</th><th></th></tr>${q}</table>` : ''}`;
}
const admSel = (k: string, opts: [string, string][]) => `<select data-adm="${k}" aria-label="${k}">${opts.map(([v, n]) => `<option value="${esc(v)}"${admForm[k] === v ? ' selected' : ''}>${esc(n)}</option>`).join('')}</select>`;
/** Each tier's first event from a launch day (shared/tournament.ts OPEN_AFTER_DAYS), for the admin page. */
const tourFirsts = (launch: number | null) => launch == null ? 'No launch day: each tier follows the calendar once on.'
  : `Pass: ${SEASONS.map((x) => `${x.name} to ${new Date(`${x.end}T00:00:00Z`).toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`).join(', then ')}. First events: ${TIER_IDS.slice().reverse().map((id) => `${TOUR_TIERS[id].name} ${new Date(nextStartFrom(id, Math.max(launch, serverNow()), launch)).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}`).join(' · ')}`;
const admNum = (k: string, label: string, w = 110) => `<input type="number" min="0" data-adm="${k}" value="${admVal(k)}" aria-label="${label}" placeholder="${label}" style="width:${w}px">`;

/** Another event of the same kind runs at the same time on some of the same hunts. */
function evOverlaps(e: ServerEvent, l: readonly ServerEvent[]): boolean {
  return l.some((o) => o.id !== e.id && o.kind === e.kind && o.start < e.end && e.start < o.end && (!o.region || !e.region || o.region === e.region));
}

/** Playtest speed (shared/playtest.ts): presets and a custom number, for this account only. */
function speedAdmin() {
  const t = S.trainer!, cur = t.speed ?? 1;
  return `<h4 class="adm-h">Playtest speed</h4>
    <div class="actions speed-row">${PLAYTEST.presets.map((n) => `<button class="btn small${cur === n ? ' primary' : ''}" data-act="adm-speed" data-id="${n}"${cur === n ? ' aria-pressed="true"' : ''}>×${full(n)}</button>`).join('')}
      <label class="adm-lab">Custom ×<input type="number" min="1" max="${PLAYTEST.max}" step="1" data-adm="speed" value="${admVal('speed')}" placeholder="1 to ${PLAYTEST.max}" aria-label="Custom speed" style="width:110px"></label><button class="btn small" data-act="adm-speed">Set</button>
      <span class="muted">Now ×${full(cur)}${t.playtest ? ' · test account' : ''}</span></div>
    <p class="hint" style="margin-top:6px">Your account only. Kills, XP, coins, drops, eggs and the Boss Ladder run that many times faster (boosts, quests and the pass keep real time). Above ×1 the account becomes a test account for good: everything it holds is admin-flagged, and it is left off boards, Records, broadcasts, the Arena, Halls, Guild War and tournaments. ×1 stops the speed; the flag stays.</p>`;
}

/** Ranked seasons: the switch (on by default), the running season and its theme, and ending it now. */
function rankedAdmin() {
  const r = S.admin?.ranked;
  const on = r?.on ?? S.ranked;
  if (!admForm.rankedTheme && r) admForm.rankedTheme = r.theme;
  const state = r ? `Season ${r.season} · ends ${utcWhen(r.endsAt)} · ${r.players} on the ladder` : '';
  return `<h4 class="adm-h">Ranked seasons</h4>
    <div class="actions"><span class="muted">${on ? 'On (the default): the Arena opens at Lv 40 and plays trophies.' : 'Off: the weekly Arena runs as before.'}</span>${on ? '<button class="btn small" data-act="adm-ranked" data-id="off">Turn off</button>' : '<button class="btn small primary" data-act="adm-ranked" data-id="on">Turn on</button>'}</div>
    ${on ? `<div class="actions"><span class="muted">${esc(state)}</span>${admSel('rankedTheme', THEMES.map((x) => [x.id, x.name]))}<button class="btn small" data-act="adm-ranked-theme">Set theme</button><button class="btn small" data-act="adm-ranked-end">End season now…</button></div>` : ''}
    ${on && r ? `<div class="actions"><span class="muted">${r.strength !== false ? 'Matching on strength (the default): a line-up stronger than its trophies meets its equals and catches up fast.' : 'Matching on trophies alone.'}</span><button class="btn small" data-act="adm-ranked-strength" data-id="${r.strength !== false ? 'off' : 'on'}">${r.strength !== false ? 'Turn off' : 'Turn on'}</button></div>` : ''}`;
}

function adminTools() {
  const ff = S.lastFF;
  const ffMax = S.admin?.ffMax ?? 1440;
  return `<h3>Dev tools</h3>
    <p class="hint">Everything made here (creatures, items, outfits, fast-forward loot) is flagged admin-granted: it never lists on the market and stays out of the economy numbers. Every command is written to the admin log.</p>
    ${sampleAdminRow()}
    ${speedAdmin()}
    <h4 class="adm-h">Trainer</h4>
    <div class="actions">${admNum('level', 'Trainer level')}<button class="btn small" data-act="adm-level">Set level (XP 0)</button>
      <button class="btn small" data-act="adm-heal">Heal team</button>
      <button class="btn small" data-act="adm-reset" style="margin-left:auto">Reset account…</button></div>
    <div class="actions">${admNum('coins', 'Coins', 130)}${admNum('diamonds', 'Diamonds')}<button class="btn small" data-act="adm-grant">Grant coins / diamonds</button></div>
    <h4 class="adm-h">Name a new trainer</h4>
    <div class="actions"><input type="email" data-adm="nameEmail" value="${admVal('nameEmail')}" placeholder="Their sign-up email" aria-label="Sign-up email" style="width:220px"><input data-adm="nameNew" value="${admVal('nameNew')}" placeholder="Name" aria-label="Name" maxlength="16" style="width:140px"><button class="btn small" data-act="adm-name-new">Give name</button></div>
    <p class="hint">For a trainer who signed up and hasn't chosen a name yet. Staff names such as admin can only be given here.</p>
    ${rankedAdmin()}
    <h4 class="adm-h">Tournaments</h4>
    <div class="actions"><span class="muted">${S.admin?.tour?.on ? 'On: sign-ups open on the calendar.' : 'Off: every tier shows as coming soon, nothing opens or charges.'}</span>${S.admin?.tour?.on ? '<button class="btn small" data-act="adm-tour-off">Turn off (refunds open entries)</button>' : '<button class="btn small" data-act="adm-tour-on">Turn tournaments on</button>'}</div>
    <div class="actions"><label class="adm-lab">Launch day <input type="date" data-adm="tourLaunch" value="${esc(admForm.tourLaunch ?? (S.admin?.tour?.launch ? new Date(S.admin.tour.launch).toISOString().slice(0, 10) : ''))}" aria-label="Launch day"></label><button class="btn small" data-act="adm-tour-launch">Set</button>${S.admin?.tour?.launch ? '<button class="btn small" data-act="adm-tour-launch-clear">Clear</button>' : ''}<span class="muted">${tourFirsts(S.admin?.tour?.launch ?? null)}</span></div>
    <h4 class="adm-h">Synergies</h4>
    <div class="actions"><span class="muted">${S.admin?.synergyOn === false ? 'Off: PvP battles fight without synergies.' : 'On in the Arena, Halls, duels, training, tournaments and Trail trainers.'}</span><button class="btn small" data-act="adm-synergy" data-id="${S.admin?.synergyOn === false ? '1' : '0'}">${S.admin?.synergyOn === false ? 'Turn synergies on' : 'Turn synergies off'}</button></div>
    <h4 class="adm-h">Board</h4>
    <div class="actions"><span class="muted">${S.admin?.boardOn === false ? 'Off: PvP fights fronts in order, as before the board.' : 'On: PvP fights on two rows (Arena, ranked, Halls, duels, training, tournaments, Trail trainers).'}</span><button class="btn small" data-act="adm-board" data-id="${S.admin?.boardOn === false ? '1' : '0'}">${S.admin?.boardOn === false ? 'Turn the board on' : 'Turn the board off'}</button></div>
    ${skinsAdmin()}
    <h4 class="adm-h">Founder Box</h4>
    <div class="actions"><span class="muted">${S.admin?.founderOpen === false ? 'Retired: hidden from the shop. Owners keep it.' : `On sale: ${FOUNDER.price} diamonds, one per account.`}</span>${S.admin?.founderOpen === false ? '<button class="btn small" data-act="adm-founder" data-id="1">Put back on sale</button>' : '<button class="btn small" data-act="adm-founder" data-id="0">Retire</button>'}</div>
    ${gemsAdmin()}
    <h4 class="adm-h">Season one</h4>
    <div class="actions"><span class="muted">Held items ${S.admin?.heldOn === false ? 'off: battles run as before them' : 'on in PvP'}.</span><button class="btn small" data-act="adm-held" data-id="${S.admin?.heldOn === false ? '1' : '0'}">${S.admin?.heldOn === false ? 'Turn held items on' : 'Turn held items off'}</button></div>
    <div class="actions"><span class="muted">The Trail ${S.admin?.trailOn === false ? 'closed: the Bosses page shows' : 'open in place of the Bosses page'}.</span><button class="btn small" data-act="adm-trail" data-id="${S.admin?.trailOn === false ? '1' : '0'}">${S.admin?.trailOn === false ? 'Open the Trail' : 'Close the Trail'}</button><button class="btn small" data-act="adm-trail-reset">Clear my Trail record</button></div>
    <div class="actions">${admSel('heldItem', HELD_ITEMS.map((d) => [d.id, d.name]))}<label class="adm-lab">Quality ${admNum('heldQ', 'Quality', 70)}</label>${admSel('heldEl', ELEMENT_IDS.map((e): [string, string] => [e, ELEMENTS[e].name]))}<button class="btn small" data-act="adm-held-grant">Grant held item</button></div>
    ${(S.trainer?.held?.length ?? 0) ? `<div class="actions">${admSel('heldDel', [['', 'Choose one of yours…'], ...S.trainer!.held!.map((h): [string, string] => [h.id, `${heldName(h)} ${h.quality}%`])])}<button class="btn small" data-act="adm-held-remove">Remove held item</button></div>` : ''}
    ${HELD_ITEMS.some((d) => (S.trainer?.bag[heldPiece(d.id)] ?? 0) > 0) ? `<div class="actions">${admSel('pieceDel', [['', 'Choose pieces of yours…'], ...HELD_ITEMS.filter((d) => (S.trainer!.bag[heldPiece(d.id)] ?? 0) > 0).map((d): [string, string] => [heldPiece(d.id), `${d.name} Piece ×${S.trainer!.bag[heldPiece(d.id)]}`])])}${admNum('pieceQty', 'How many', 90)}<button class="btn small" data-act="adm-piece-remove">Remove pieces</button></div>` : ''}
    <p class="hint">Pieces are bag items: grant them under Items.</p>
    <h4 class="adm-h">Events</h4>
    <div class="actions">${admSel('evKind', Object.entries(EVENT_KINDS).map(([k, d]) => [k, d.name]))}<label class="adm-lab">×<input type="number" step="0.05" min="${EVENTS.minMult}" max="${EVENTS.maxMult}" data-adm="evMult" value="${admVal('evMult')}" aria-label="Multiplier" style="width:80px"></label>
      <label class="adm-lab">Start<input type="text" data-adm="evStart" value="${admVal('evStart')}" placeholder="Now, or YYYY-MM-DD HH:MM UTC" aria-label="Start UTC" style="width:260px"></label><label class="adm-lab">${admNum('evHours', 'Hours', 70)}h</label>${admSel('evRegion', [['', 'All regions'], ...REGIONS.map((r): [string, string] => [r.id, r.name])])}<button class="btn small" data-act="adm-event">Schedule</button></div>
    <p class="hint">×${EVENTS.minMult} to ×${EVENTS.maxMult}, ${EVENTS.minMinutes} min to ${EVENTS.maxDays} days. Multiplies with every other boost.</p>
    ${(S.admin?.events ?? serverEvents()).length ? `<table class="tbl">${(S.admin?.events ?? serverEvents()).map((e) => `<tr><td>${esc(eventLabel(e))}${evOverlaps(e, S.admin?.events ?? serverEvents()) ? ` <span class="ev-overlap" title="${esc(`Overlaps another ${EVENT_KINDS[e.kind].name} event: together capped at ×${EVENTS.maxMult}`)}">overlap</span>` : ''}</td><td>${esc(utcWhen(e.start))} → ${esc(utcWhen(e.end))}</td><td><button class="btn small" data-act="adm-event-del" data-id="${e.id}">Remove</button></td></tr>`).join('')}</table>` : ''}
    <h4 class="adm-h">Items</h4>
    <div class="actions">${admSel('item', ITEMS.map((i) => [i.id, i.name]))}${admNum('itemQty', 'Quantity', 90)}<button class="btn small" data-act="adm-item">Grant items</button></div>
    <div class="actions">${admSel('egg', EGGS.map((d) => [d.id, d.name]))}${admSel('eggRegion', REGIONS.map((r) => [r.id, r.name]))}<button class="btn small" data-act="adm-egg">Grant egg (admin-flagged)</button></div>
    <div class="actions">${admSel('outfit', OUTFITS.filter((o) => o.source.kind === 'shop').map((o) => [o.id, o.name]))}<button class="btn small" data-act="adm-outfit">Grant outfit</button></div>
    <div class="actions"><button class="btn small" data-act="adm-aura">Grant Aura Charm (${CONFIG.auraCharm.hours} h, free)</button></div>
    <h4 class="adm-h">Creature</h4>
    <div class="actions">${admSel('species', Object.values(SPECIES).map((sp) => [sp.id, `${sp.name} (${sp.rarity}, ${sp.region})`]))}
      ${admNum('cLevel', 'Level', 80)}
      ${admSel('bloodline', CONFIG.bloodline.map((b, i) => [String(i), `Bloodline ${b.name}`]))}
      ${admSel('talent', [['random', 'Random talent'], ['average', 'Average talent'], ['perfect', 'Perfect talent']])}
      <label class="row"><input type="checkbox" data-adm="aura"${admForm.aura ? ' checked' : ''}> Aura</label>
      <button class="btn small" data-act="adm-creature">Grant creature</button></div>
    <h4 class="adm-h">Team and hunt</h4>
    <div class="actions">${admNum('teamLevel', 'Team level', 90)}<button class="btn small" data-act="adm-team">Set whole team level</button></div>
    <div class="actions">${admSel('ground', GROUNDS.map((g) => [g.id, `${AREA[g.area]?.name ?? g.area} · Lv ${g.level}`]))}<button class="btn small" data-act="adm-hunt">Jump to hunt (ignores lock)</button></div>
    <h4 class="adm-h">Fast-forward</h4>
    <div class="actions">${admNum('ffMinutes', 'Minutes', 90)}<button class="btn small primary" data-act="adm-ff">Run</button>
      ${[[10, '10 min'], [60, '1 h'], [480, '8 h'], [ffMax, `${ffMax / 60} h`]].map(([m, n]) => `<button class="btn small" data-act="adm-ff" data-id="${m}">${n}</button>`).join('')}</div>
    <p class="hint" style="margin-top:6px">Runs your current hunt on the server for that much simulated time (max ${ffMax / 60} h per run), then syncs. Other players keep ticking meanwhile.</p>
    ${ff ? `<table class="tbl"><tr><th>Last run</th><th>Kills</th><th>XP</th><th>Coins</th><th>Catches</th><th>Wipes</th><th>Level</th><th>Took</th></tr>
      <tr><td>${ff.minutes} min</td><td>${full(ff.kills)}</td><td>+${full(ff.xp)}${ff.xpLost ? ` (−${full(ff.xpLost)})` : ''}</td><td>${full(ff.coins)}</td><td>${ff.catches}${ff.auras ? ` (${ff.auras} aura)` : ''}</td><td>${ff.wipes}${ff.retreats ? ` / ${ff.retreats} retreats` : ''}</td><td>${ff.levelFrom} → ${ff.levelTo}</td><td>${(ff.wallMs / 1000).toFixed(1)} s</td></tr></table>` : ''}`;
}

/** Partner Twitch channels (server/streams.ts); only sent while streams are on. */
function streamsAdminSection(s: StreamsAdmin | undefined) {
  if (!s) return '';
  const rows = s.partners.map((p) => `<tr><td><a href="https://www.twitch.tv/${esc(p.login)}" target="_blank" rel="noopener">${esc(p.login)}</a></td><td>${esc(p.name || '—')}</td><td>${p.live ? `<span class="lv-adm-on">Live · ${full(p.viewers)}</span>` : '<span class="muted">Offline</span>'}</td><td><button class="btn small" data-act="adm-stream-del" data-id="${esc(p.login)}">Remove</button></td></tr>`).join('');
  return `<div class="row between" style="margin-top:16px"><h3>Partner streams</h3><span class="muted">${s.watching} watching${s.lastPoll ? ` · checked ${new Date(s.lastPoll).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}${s.error ? ` · ${esc(s.error)}` : ''}</span></div>
    <div class="actions"><input type="text" data-adm="lvLogin" value="${admVal('lvLogin')}" placeholder="Twitch channel" aria-label="Twitch channel" style="width:180px"><input type="text" data-adm="lvName" value="${admVal('lvName')}" placeholder="Display name (optional)" aria-label="Display name" style="width:200px"><button class="btn small" data-act="adm-stream-add">Add partner</button></div>
    <table class="tbl"><tr><th>Channel</th><th>Name</th><th>Status</th><th></th></tr>${rows || '<tr><td colspan=4>No partners yet</td></tr>'}</table>`;
}

function adminPane() {
  const a = S.admin;
  if (!a) return '<p class="hint">Loading…</p>';
  const log = (a.log ?? []).map((l) => `<tr><td>${new Date(l.ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td><td>${esc(l.action)}</td><td><code>${esc(l.data.slice(0, 160))}</code></td></tr>`).join('');
  const rows = a.econ.slice(-24).reverse().map((e) => {
    const mint = Object.values(e.mint).reduce((x, y) => x + y, 0), burn = Object.values(e.burn).reduce((x, y) => x + y, 0);
    return `<tr><td>${new Date(e.hour * 3_600_000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' })}</td><td>${full(mint)}</td><td>${full(burn)}</td><td>${mint ? Math.round((burn / mint) * 100) : 0}%</td></tr>`;
  }).join('');
  const prices = Object.entries(a.prices).map(([sp, ps]) => { const sorted = [...ps].sort((x, y) => x - y); return `<tr><td>${SPECIES[sp]?.name ?? sp}</td><td>${ps.length}</td><td>${full(sorted[Math.floor(sorted.length / 2)])}</td></tr>`; }).join('');
  return `${adminTools()}
    <h3 style="margin-top:16px">Your recent admin commands</h3>
    <table class="tbl"><tr><th>When</th><th>Command</th><th>Details</th></tr>${log || '<tr><td colspan=3>None yet</td></tr>'}</table>
    ${streamsAdminSection(a.streams)}
    ${exchangeAdminSection(a.exchange)}
    ${storeAdminSection(a.store)}
    ${diamondAdminSection(a.diamonds)}
    ${playerReportSection()}
    ${reportsAdminSection(a.reports)}
    <div class="row between" style="margin-top:16px"><h3>Economy</h3><button class="btn small" data-act="admin">Refresh</button></div>
    <p class="hint">${a.accounts} accounts, ${a.online} online. Burn rate is coins removed as a share of coins created; aim for 60–90%.</p>
    <table class="tbl"><tr><th>Hour</th><th>Minted</th><th>Burned</th><th>Burn rate</th></tr>${rows}</table>
    <h3 style="margin-top:16px">Creature prices (last 500 sales)</h3>
    <table class="tbl"><tr><th>Species</th><th>Sales</th><th>Median</th></tr>${prices || '<tr><td colspan=3>No sales yet</td></tr>'}</table>`;
}

// ---------------- creature page ----------------
export function refreshModal() { if (S.page === 'creature') render(true); }
/** Close returns to where the creature was opened from (same list, same scroll), or to Creatures. */
function closeCreature() { if (depth > 0) history.back(); else go('#/creatures'); }

export function openCreature(id: number) { go(`#/creature/${id}`); }

/** Trainer XP per hour: this session's rate once it has five minutes, else the analyser's estimate for the lead here. */
function trainerRate(): { rate: number; measured: boolean } | null {
  const r = sessionXpPerHour();
  if (r && r > 0) return { rate: r, measured: true };
  const lead = leadCreature(), g = GROUND[S.trainer!.ground];
  return lead && g ? { rate: estimate(lead, g).xpPerHour, measured: false } : null;
}

// ---------------- events ----------------
function onClick(e: MouseEvent) {
  const hit = (e.target as HTMLElement).closest('[data-tab],[data-act],[data-open],[data-close],[data-region],[data-area],[data-mset],[data-msort],[data-mpage],[data-mclear],[data-mqty],#strip,#combatlog,.menuwrap') as HTMLElement | null;
  // <html> carries data-region for the themes (client/hud.ts): a click on empty space is not a region pick
  // (it used to snap the map back to the hunt's own area).
  const el = hit === document.documentElement || hit === document.body ? null : hit;
  // A click anywhere else closes an open app-bar menu.
  if (menu && !el?.closest('.menuwrap') && el?.dataset.act !== 'menu-more') { menu = ''; render(true); }
  if (!el) return;
  if (el.id === 'strip') { if (!(e.target as HTMLElement).closest('a, button')) go('#/hunt'); return; }
  if (el.id === 'combatlog') { toggleLog(); return; }
  if (el.classList.contains('menuwrap')) return;
  if (el.dataset.close !== undefined) { closeCreature(); return; }
  if (el.dataset.tab) return go(TAB_ROUTE[el.dataset.tab] ?? '#/hunt');
  if (S.page === 'market' && marketClick(el)) return;
  if (el.dataset.act?.startsWith('ph-') && hubClick(el.dataset.act, el.dataset.id ?? '', el)) return render(true);
  if (el.dataset.act?.startsWith('live-') && streamsClick(el)) return;
  if (el.dataset.act?.startsWith('ex-') && exchangeClick(el)) return;
  if (el.dataset.act?.startsWith('prep-') && playerReportClick(el)) return;
  if (el.dataset.act?.startsWith('peek') && peekClick(el.dataset.act, el.dataset.id ?? '')) return;
  if (el.dataset.act?.startsWith('bv-') && boardViewClick(el)) return;
  if (el.dataset.act && creaturesClick(e, el)) return;
  if (el.dataset.act && mapClick(el)) return render(true);
  if (el.dataset.act && pagesClick(el)) return;
  if (el.dataset.act && communityClick(el)) return;
  if (el.dataset.act && outfitsClick(el)) return;
  if (el.dataset.act && guildClick(el, e)) return;
  if (el.dataset.act?.startsWith('arena') && arenaClick(el.dataset.act, el.dataset.id ?? '')) return;
  if (el.dataset.act?.startsWith('halls') && hallsClick(el.dataset.act, el.dataset.id ?? '')) return;
  if (el.dataset.act?.startsWith('wb-') && worldBossClick(el.dataset.act)) return;
  if (el.dataset.act?.startsWith('dm-')) { if (el.dataset.act === 'dm-open') closeProfileCard(); if (messagesClick(el)) return; }
  if (el.dataset.act?.startsWith('fr-') && socialClick(el)) return;
  if (el.dataset.act?.startsWith('tr-') && trainingClick(el)) return;
  if (el.dataset.act?.startsWith('plan-') && planClick(el)) return;
  if (el.dataset.act?.startsWith('tn-') && tournamentsClick(el)) return;
  if (el.dataset.act?.startsWith('pass-') && passClick(el)) return;
  if (el.dataset.act?.startsWith('held-') && heldClick(el)) return;
  if (el.dataset.act?.startsWith('trail-') && trailClick(el)) return;
  if (el.dataset.open) { if (!creaturesOpen(e, Number(el.dataset.open))) openCreature(Number(el.dataset.open)); return; }
  if (el.dataset.region) { S.mapRegion = el.dataset.region; selectedArea = null; return render(true); }
  if (el.dataset.area) { selectedArea = el.dataset.area; if (el.classList.contains('mnode')) openSection('map', 'area'); return render(true); }
  const t = S.trainer!;
  const id = el.dataset.id ?? '';
  const nid = Number(id);
  switch (el.dataset.act) {
    case 'daily': menu = ''; return send({ t: 'daily' });
    case 'menu-gift': menu = menu === 'gift' ? '' : 'gift'; return render(true);
    case 'menu-account': menu = menu === 'account' ? '' : 'account'; return render(true);
    case 'menu-more': menu = menu === 'more' ? '' : 'more'; return render(true);
    case 'menu-nav': menu = menu === 'nav' ? '' : 'nav'; return render(true);
    case 'away-close': return closeAway();
    case 'away-catches': showCaughtSince(awaySince()); closeAway(); return go('#/creatures');
    case 'psec': psecClick('psec', id); return render(true);
    case 'ptab':
      ptab = id;
      try { localStorage.setItem('mi-ptab', id); } catch { /* storage off */ }
      if (id === 'auto' || id === 'world' || id === 'chat') { setRailTab(id); renderFeed(); }
      return render(true);
    case 'dex-open': return go(`#/bestiary/${id}`);
    case 'dex-back': return go('#/bestiary');
    case 'dex-all': setDexAll(id === '1'); return render(true);
    case 'dex-claim': return send({ t: 'dexClaim', region: id });
    case 'quest-claim': return send({ t: 'questClaim' });
    case 'starter-claim': return send({ t: 'starter', op: 'claim' });
    case 'starter-skip': return send({ t: 'starter', op: 'skip' });
    case 'calc-species': calcSpecies(id); return go('#/calculator');
    case 'calc-iv': case 'calc-bl': case 'calc-random': if (calcAct(el.dataset.act, id)) render(true); return;
    case 'share-listing': send({ t: 'shareListing', id: nid }); selectChannel(); setRailTab('trade'); renderFeed(); toast('Shared in Trade'); return;
    case 'showcase': send({ t: 'showcase', id: nid }); selectChannel(); setRailTab('world'); renderFeed(); toast('Shown in chat'); return;
    case 'calc-load': { const c = S.creatures.get(nid); if (!c) return; loadCalc(c); return go('#/calculator'); }
    case 'seal': return send({ t: 'settings', settings: { seal: id } });
    case 'auto-sec': toggleAutoSection(id); return render(true);
    case 'session-reset': return resetSession();
    case 'session-toggle': return toggleSession();
    case 'logtab': return setLogTab(id);
    case 'cap-sort': return setCapSort(id);
    case 'corner': void toggleCorner().then((r) => { if (r === 'blocked') toast('Allow pop-ups for this site to open the corner window', 'err'); }); return;
    case 'saver-on': case 'saver-off': setBatterySaver(el.dataset.act === 'saver-on'); return render(true);
    case 'hunt-emotes': toggleHuntEmotes(); return render(true);
    case 'hunt-emote': playHuntEmote(el.dataset.id!); toggleHuntEmotes(false); return render(true);
    case 'zoom-in': return setZoom(getZoom() + 1);
    case 'zoom-out': return setZoom(getZoom() - 1);
    case 'logout': localStorage.removeItem('mi-token'); location.reload(); return;
    case 'hunt': return send({ t: 'ground', id });
    case 'tutorial-replay': replayTutorial(); return go('#/hunt');
    case 'feedback': void openFeedback(); return;
    case 'switch-hunt': return switchHunt(id);
    case 'map-hunt': switchHunt(id); return go('#/hunt');
    case 'loud-toggle': {
      setLoud(!loudOn()); renderFeed();
      const f = el.closest('form')?.querySelector('input'); (f as HTMLInputElement | null)?.focus(); return;
    }
    case 'rtab':
      if (id === 'inbox') { if (chatSel().k === 'ch') selectList('inbox'); else if (chatSel().k === 'list') selectList('inbox'); setRailTab('chat'); }
      else { if (id !== 'auto' && id !== 'chat') selectChannel(); setRailTab(id); }
      renderFeed(); return;
    case 'suggest-dismiss': dismissSuggestion(id); return render(true);
    case 'suggest-show': showSuggestion(); return render(true);
    case 'hunt-again': return send({ t: 'ground', id: S.trainer!.ground });
    case 'buy': return send({ t: 'buy', item: id, qty: Number(el.dataset.qty) });
    case 'sell': return send({ t: 'sell', item: id, qty: 1 });
    // Diamond purchases take a second click on the same button (inline, no dialog).
    case 'pack-gems': return gemPackClick(id);
    case 'dp-pay': return payByClick(id);
    case 'shop': if (confirmClick(`shop:${id}`)) send({ t: 'shop', offer: id, ...(el.dataset.price ? { price: Number(el.dataset.price) } : {}) }); return;
    case 'boss': {
      const key = ITEM[BOSS_TOKEN[id]]?.name ?? 'boss key', name = SPECIES[id]?.name ?? id;
      void openDialog({
        title: `Challenge ${name}?`,
        body: `<p>${esc(key)}: ${full(t.bag[BOSS_TOKEN[id]] ?? 0)}</p>`,
        actions: [{ label: `Challenge (uses 1 ${key})`, value: 'go', kind: 'primary' }],
      }).then((r) => { if (r.value) send({ t: 'boss', id }); });
      return;
    }
    case 'scrap': return send({ t: 'scrapRelic', relic: id });
    case 'unlist': return send({ t: 'unlist', id: nid });
    case 'purchase': return void buyListing(nid);
    case 'market-refresh': return send({ t: 'market' });
    case 'admin': return send({ t: 'admin' });
    case 'adm-level': return send({ t: 'admSetLevel', level: Number(admForm.level) });
    case 'adm-grant': return send({ t: 'admGrant', coins: Number(admForm.coins), diamonds: Number(admForm.diamonds) });
    case 'adm-item': return send({ t: 'admGrantItem', item: admForm.item, qty: Number(admForm.itemQty) });
    case 'adm-outfit': return send({ t: 'admGrantOutfit', id: admForm.outfit });
    case 'adm-aura': return send({ t: 'admGrantAuraCharm' });
    case 'adm-egg': return send({ t: 'admGrantEgg', egg: admForm.egg, region: admForm.eggRegion });
    case 'adm-creature': return send({ t: 'admGrantCreature', species: admForm.species, level: Number(admForm.cLevel), bloodline: Number(admForm.bloodline), aura: !!admForm.aura, talent: admForm.talent as 'random' | 'average' | 'perfect' });
    case 'adm-team': return send({ t: 'admTeamLevel', level: Number(admForm.teamLevel) });
    case 'adm-hunt': return send({ t: 'admHunt', id: admForm.ground });
    case 'adm-heal': return send({ t: 'admHeal' });
    case 'adm-event': {
      const raw = admForm.evStart.trim();
      const start = raw ? Date.parse(`${raw.replace(' ', 'T')}${raw.length <= 10 ? 'T00:00' : ''}Z`) : serverNow();
      if (!Number.isFinite(start)) return toast('Start must be YYYY-MM-DD HH:MM (UTC).', 'err');
      return send({ t: 'admEvent', kind: admForm.evKind, mult: Number(admForm.evMult), start, end: start + Number(admForm.evHours) * 3_600_000, ...(admForm.evRegion ? { region: admForm.evRegion } : {}) });
    }
    case 'adm-event-del': return send({ t: 'admEventDel', id: nid });
    case 'adm-stream-add': return send({ t: 'admStreamAdd', login: admForm.lvLogin ?? '', name: admForm.lvName ?? '' });
    case 'adm-stream-del': return send({ t: 'admStreamDel', login: id });
    case 'adm-founder': return send({ t: 'admFounder', open: id === '1' });
    case 'adm-ranked': return send({ t: 'admRanked', on: id === 'on' });
    case 'adm-ranked-strength': return send({ t: 'admRanked', strength: id === 'on' });
    case 'adm-ranked-theme': return send({ t: 'admRanked', theme: admForm.rankedTheme || THEMES[0].id });
    case 'adm-ranked-end':
      void openDialog({ title: 'End the ranked season now?', body: '<p>Top prizes and Legend titles are paid, every standing is soft reset, and the next season starts with the next theme rule.</p>', danger: true, actions: [{ label: 'End season', value: 'end', kind: 'danger' }] })
        .then((r) => { if (r.value) send({ t: 'admRanked', endSeason: true }); });
      return;
    case 'adm-synergy': return send({ t: 'admSynergy', on: id === '1' });
    case 'adm-board': return send({ t: 'admBoard', on: id === '1' });
    case 'adm-held': return send({ t: 'admHeld', on: id === '1' });
    case 'adm-trail': return send({ t: 'admTrail', on: id === '1' });
    case 'adm-gems': return send({ t: 'admGems', on: id === '1' });
    case 'adm-store-live':
      if (id !== '1') return send({ t: 'admStoreLive', on: false });
      void openDialog({ title: 'Turn on real money?', body: '<p>Every player sees the diamond shop and pays with real cards. Test orders stop counting.</p>', danger: true, actions: [{ label: 'Turn on', value: 'on', kind: 'danger' }] })
        .then((r) => { if (r.value) send({ t: 'admStoreLive', on: true }); });
      return;
    case 'adm-gem-cashout-on': return send({ t: 'admGemCashoutOn', on: id === '1' });
    case 'adm-gem-credit': return send({ t: 'admGemCredit', name: admForm.gemName ?? '', gems: Number(admForm.gemAmt) || 0 });
    case 'adm-name-new': return send({ t: 'admNameNew', email: admForm.nameEmail ?? '', name: admForm.nameNew ?? '' });
    case 'adm-gem-pay':
      if (gemPaying.has(nid) || !confirmClick(`gempay:${id}`, CONFIRM_MONEY_MS)) return;
      // One click pays once: the button reads Paying… until the Admin page comes back (or 20 s pass).
      gemPaying.add(nid); setTimeout(() => { if (gemPaying.delete(nid)) render(true); }, 20_000); render(true);
      send({ t: 'admGemCashout', id: nid, op: 'pay' }); return;
    case 'adm-gem-refuse': return send({ t: 'admGemCashout', id: Number(id), op: 'refuse' });
    case 'adm-trail-reset': return send({ t: 'admTrailReset' });
    case 'adm-held-grant': return send({ t: 'admGrantHeld', item: admForm.heldItem, quality: Number(admForm.heldQ), el: admForm.heldEl });
    case 'adm-held-remove': if (admForm.heldDel) send({ t: 'admRemoveHeld', id: admForm.heldDel }); return;
    case 'adm-piece-remove': if (admForm.pieceDel) send({ t: 'admRemovePieces', item: admForm.pieceDel, qty: Number(admForm.pieceQty) }); return;
    case 'adm-tour-on': return send({ t: 'admTournaments', on: true });
    case 'adm-tour-off': return send({ t: 'admTournaments', on: false });
    case 'adm-tour-launch': if (admForm.tourLaunch) send({ t: 'admTourLaunch', day: admForm.tourLaunch }); return;
    case 'adm-tour-launch-clear': delete admForm.tourLaunch; return send({ t: 'admTourLaunch', day: null });
    case 'adm-reset':
      void openDialog({ title: 'Reset your account?', body: `<p>Your account goes back to a fresh start. Every creature, item and coin is lost. This can't be undone.</p><p>"Full reset" makes it a brand-new player account: it also clears ${t.playtest ? 'the test flag, ' : ''}gems, outfits, skins and arena and ranked standing, so it can trade like any player. Admin rights stay.</p>`, danger: true, actions: [{ label: 'Full reset', value: 'clean', kind: 'danger' as const }, { label: 'Reset account', value: 'reset', kind: 'danger' }] })
        .then((r) => { if (r.value) send({ t: 'admReset', ...(r.value === 'clean' ? { clean: true } : {}) }); });
      return;
    case 'adm-speed': {
      const n = Number(id || admForm.speed);
      if (!Number.isFinite(n) || n < 1 || n > PLAYTEST.max) return toast(`Pick a speed from 1 to ${PLAYTEST.max}.`, 'err');
      if (n > 1 && !t.playtest) {
        void openDialog({ title: `Playtest speed ×${Math.floor(n)}?`, body: '<p>This account becomes a test account for good: everything it holds is admin-flagged (no market, Exchange or gifts), and it is left off boards, Records, the Arena, Halls, Guild War and tournaments. Turning the speed back to ×1 keeps the flag.</p>', danger: true, actions: [{ label: 'Speed up', value: 'go', kind: 'danger' }] })
          .then((r) => { if (r.value) send({ t: 'admSpeed', speed: n }); });
        return;
      }
      return send({ t: 'admSpeed', speed: n });
    }
    case 'adm-ff': { const minutes = Number(id || admForm.ffMinutes); if (id) admForm.ffMinutes = id; return send({ t: 'admFastForward', minutes }); }
    case 'team-add': if (t.team.length >= CONFIG.teamSize) return void swapIn(nid, false); return send({ t: 'team', ids: [...t.team, nid] });
    case 'team-remove': return send({ t: 'team', ids: t.team.filter((x) => x !== nid) });
    case 'lead':
      // From storage with a full team: pick who goes to storage, in one step.
      if (!t.team.includes(nid) && t.team.length >= CONFIG.teamSize) return void swapIn(nid, true);
      return send({ t: 'team', ids: [nid, ...t.team.filter((x) => x !== nid)] });
    case 'collect': return send({ t: 'collect', id: nid, on: !(t.collection ?? []).includes(nid) });
    case 'charm-on': return send({ t: 'charm', id: nid, on: true });
    case 'nick-clear': return nickClear(nid, send);
    case 'charm-off': return send({ t: 'charm', id: nid, on: false });
    case 'shrine-use': { const s = t.shrines?.find((x) => x.id === id); if (s) send({ t: 'shrine', id, active: !s.active }); return; }
    case 'shrine-craft': return send({ t: 'shrineCraft' });
    case 'shrine-clear': { const s = t.shrines?.find((x) => x.id === id); if (s) send({ t: 'shrine', id, posts: s.posts.map((x, i) => (i === Number(el.dataset.qty) ? 0 : x)) }); return; }
    case 'shrine-fuse': {
      // Unused shrines go first, so the ones holding creatures stay.
      const ids = (t.shrines ?? []).filter((s) => s.tier === nid).sort((a, b) => Number(a.active) - Number(b.active)).slice(0, SHRINES_PER_FUSE).map((s) => s.id);
      const from = SHRINE_TIERS[nid], to = SHRINE_TIERS[nid + 1];
      void openDialog({
        title: `Fuse ${SHRINES_PER_FUSE} ${from.name}s?`,
        body: `<p>${SHRINES_PER_FUSE} ${esc(from.name)}s → 1 ${esc(to.name)} (+${Math.round(to.share * 100)}% XP)</p>`,
        actions: [{ label: 'Fuse', value: 'fuse', kind: 'primary' }],
      }).then((r) => { if (r.value) send({ t: 'shrineFuse', ids }); });
      return;
    }
    // The pager replaces the entry, so Close still returns to where the creature was opened from.
    case 'cp-page': return go(`#/creature/${id}`, true);
    case 'unequip': return send({ t: 'equip', id: nid, relic: null });
    case 'ascend': return ascendDialog(nid, send);
    case 'star': return send({ t: 'star', id: nid });
    case 'awaken': return awakenConfirm(nid, send);
    case 'refine-pick': pickRefineStat(nid, Number(el.dataset.stat)); return render(true);
    case 'refine-item': pickRefineItem(nid, el.dataset.item ?? ''); return render(true);
    // Refine spends many star items: a second click on the same button confirms (inline, no dialog).
    case 'refine': {
      const stat = Number(el.dataset.stat), steps = Number(el.dataset.qty) || 1, item = el.dataset.item;
      if (confirmClick(`refine:${nid}:${stat}:${steps}`)) send({ t: 'refine', id: nid, stat, steps, item });
      return;
    }
    case 'train-pick': pickTrainStat(nid, Number(el.dataset.stat)); return render(true);
    case 'train-item': pickTrainStone(nid, el.dataset.item ?? ''); return render(true);
    // Stone training spends stones and coins: a second click on the same button confirms.
    case 'train': {
      const stat = Number(el.dataset.stat), steps = Number(el.dataset.qty) || 1, item = el.dataset.item;
      if (confirmClick(`train:${nid}:${stat}:${steps}`)) send({ t: 'train', id: nid, stat, steps, item });
      return;
    }
    // A Rune Tablet is used up: a second click on the same button confirms.
    case 'tablet': {
      const elId = el.dataset.el ?? '';
      if (confirmClick(`tablet:${nid}:${elId}`)) send({ t: 'tablet', id: nid, element: elId });
      return;
    }
    case 'release': {
      if ((t.collection ?? []).includes(nid)) return toast('Unstar it to release', 'err');
      const c = S.creatures.get(nid);
      if (!c) return;
      const coins = 5 + c.level * 2;
      // Cancel leaves the creature open, exactly as it was.
      void openDialog({
        title: `Release ${cname(c)}?`,
        body: `<p>You get ${full(coins)} coins.${c.relic ? ' Its relic goes back to your bag.' : ''} This can't be undone.</p>`,
        danger: true,
        actions: [{ label: `Release for ${full(coins)} coins`, value: 'release', kind: 'danger' }],
      }).then((r) => { if (r.value) { send({ t: 'release', ids: [nid] }); closeCreature(); } });
      return;
    }
    case 'list-creature': {
      const c = S.creatures.get(nid);
      if (!c) return;
      void listCreature(nid).then((ok) => { if (ok) closeCreature(); });
      return;
    }
    case 'list-item': {
      // Admin-granted copies never list, so only the rest count.
      const own = Math.max(0, (t.bag[id] ?? 0) - (t.devItems?.[id] ?? 0));
      return void listDialog({ kind: 'item', ref: id, name: ITEM[id]?.name ?? id, maxQty: own, cur: el.dataset.cur === 'gems' ? 'gems' : 'coins', note: own < (t.bag[id] ?? 0) ? `${full((t.bag[id] ?? 0) - own)} of yours came from admin commands and can't be listed.` : '' });
    }
    case 'list-shrine': {
      const s = t.shrines?.find((x) => x.id === id);
      return void listDialog({ kind: 'shrine', ref: id, pkey: s ? String(s.tier) : '', name: s ? SHRINE_TIERS[s.tier].name : 'Shrine', note: 'Leaves its slot.' });
    }
    case 'list-relic': {
      const r = t.relics.find((x) => x.id === id);
      return void listDialog({ kind: 'relic', ref: id, pkey: r?.stat ?? '', name: r ? `${relicGrade(r) ? `${relicGrade(r)} ` : ''}${relicName(r)} Q${r.quality}` : 'Relic' });
    }
    case 'list-held': {
      const h = t.held?.find((x) => x.id === id);
      return void listDialog({ kind: 'held', ref: id, pkey: h?.item ?? '', name: h ? heldLabel(h) : 'Held item' });
    }
  }
}

function onChange(e: Event) {
  const el = e.target as HTMLInputElement | HTMLSelectElement;
  if (el.dataset.adm) { admForm[el.dataset.adm] = el.type === 'checkbox' ? ((el as HTMLInputElement).checked ? '1' : '') : el.value; return; }
  if (el.dataset.setting) send({ t: 'settings', settings: { [el.dataset.setting]: el.dataset.setting === 'autoSellBelow' ? Number(el.value) : el.value } });
  if (el.dataset.use) { const kind = el.dataset.use === 'potions' ? 'potions' : 'seals'; send({ t: 'settings', settings: { [kind]: useListChange(kind, 'switch', el.dataset.id ?? '', (el as HTMLInputElement).checked) } }); }
  if (el.dataset.best) { const kind = el.dataset.best === 'potions' ? 'potions' : 'seals'; send({ t: 'settings', settings: { [kind]: useListChange(kind, 'sort', '', (el as HTMLInputElement).checked ? 'strong' : 'weak') } }); }
  if (el.dataset.potionBelow !== undefined) { const v = Number(el.value); send({ t: 'settings', settings: v > 0 ? { autoPotion: true, potionBelow: v } : { autoPotion: false } }); }
  if (el.dataset.restock && S.trainer) {
    const t = S.trainer;
    const cur = t.settings.restock ?? { on: false, keep: {} };
    const next = { ...cur, keep: { ...cur.keep } };
    if (el.dataset.restock === 'on') {
      next.on = (el as HTMLInputElement).checked;
      // First switch-on with nothing set: start from the seal and potion in use.
      if (next.on && !Object.values(next.keep).some((n) => n > 0)) next.keep = firstKeep(t);
    } else if (el.dataset.restock === 'keep' && el.dataset.id) next.keep[el.dataset.id] = Math.max(0, Math.floor(Number(el.value) || 0));
    else if (el.dataset.restock === 'floor') next.floor = Math.max(0, Math.floor(Number(el.value) || 0));
    send({ t: 'settings', settings: { restock: next } });
  }
  if (el.dataset.settingBool) send({ t: 'settings', settings: { [el.dataset.settingBool]: (el as HTMLInputElement).checked } });
  if (creaturesChange(el)) return;
  if (mapChange(el)) return render(true);
  if (pagesChange(el)) return render(true);
  if (communityChange(el)) return render(true);
  if (trainingInput(el)) return render(true);
  if (planInput(el)) return render(true);
  if (tournamentsInput(el)) return render(true);
  if (socialInput(el)) return;
  if (el.dataset.dm === 'from' && messagesInput(el)) return;
  if (heldChange(el)) return;
  if (el.dataset.equip) send({ t: 'equip', id: Number(el.dataset.equip), relic: el.value || null });
}

/** Switch hunts right away, with five seconds to take it back. */
function switchHunt(id: string) {
  const t = S.trainer!;
  const from = t.ground;
  if (!GROUND[id]) return;
  // The hunt it stopped on resumes it (TrainerDTO.stopped); otherwise the same hunt is a no-op.
  if (id === from) { if (t.stopped) send({ t: 'ground', id }); return; }
  send({ t: 'ground', id });
  toast(`Switched to ${SPECIES[GROUND[id].species].name}`, '', { label: 'Undo', run: () => send({ t: 'ground', id: from }) }, 5000);
}

// ---------------- feed and toasts ----------------
const CHANNEL_INPUT: Record<Exclude<ChatChannel, 'region'>, string> = { world: 'Message World', trade: 'Message Trade', guild: 'Message Guild', help: 'Ask Help' };
const channelInput = (ch: ChatChannel) => (ch === 'region' ? `Message ${huntRegion()?.name ?? 'Region'}` : CHANNEL_INPUT[ch]);

/**
 * What the chat shows for the current selection (client/messages.ts chatSel): a channel's lines, the list of
 * conversations or requests, or one conversation. The chat panel beside the hunt and the Chat page draw the same.
 */
function chatBody(page: boolean): { key: string; html: string; form: boolean; placeholder: string; disabled: boolean } {
  const sel = chatSel();
  if (sel.k === 'list') return { key: `l:${sel.which}`, html: listHtml(sel.which, '', page), form: false, placeholder: '', disabled: true };
  if (sel.k === 'dm') {
    const blocked = isBlocked(sel.name);
    return { key: `d:${sel.name.toLowerCase()}`, html: `${page ? '' : convHeadHtml(sel.name, false)}${convLinesHtml(sel.name)}`, form: true, placeholder: blocked ? 'Blocked' : `Message ${sel.name}`, disabled: blocked };
  }
  const ch = feedChannel();
  const noGuild = ch === 'guild' && !S.trainer?.guild;
  if (noGuild) return { key: 'c:guild', html: '<div class="guild-empty" data-key="guild"><a class="btn small" href="#/guild">Find a guild</a></div>', form: true, placeholder: 'No guild', disabled: true };
  // World holds the feed lines as well as World chat; other channels hold their chat and their own notices.
  const gid = S.trainer?.guild?.id, rg = huntRegion()?.id;
  // Lines from trainers you muted or blocked are left out.
  const shown = S.broadcasts.filter((b) => (b.kind === 'chat' || b.ch ? (b.ch ?? 'world') === ch : ch === 'world') && (b.gid === undefined || b.gid === gid) && (b.rg === undefined || b.rg === rg) && !(b.kind === 'chat' && chatHidden(b.who))).slice(-80);
  if (shown.length) lastShown = { ch, ts: shown[shown.length - 1].ts };
  return { key: `c:${ch}`, html: shown.map(feedLine).join(''), form: true, placeholder: ch === 'world' && loudOn() ? loudPlaceholder() : channelInput(ch), disabled: false };
}
let lastShown = null as { ch: ChatChannel; ts: number } | null;
const feedKeys = new WeakMap<HTMLElement, string>();

/** Draw the chat body into a list; it stays at the newest line unless scrolled up, and jumps there on a new selection. */
function drawChat(list: HTMLElement, form: HTMLFormElement | null, page: boolean) {
  const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 30;
  lastShown = null;
  const b = chatBody(page);
  if (form) {
    form.hidden = !b.form;
    const inp = form.querySelector('input') as HTMLInputElement | null;
    if (inp) { inp.placeholder = b.placeholder; inp.disabled = b.disabled; inp.setAttribute('aria-label', b.placeholder || 'Chat message'); }
    loudForm(form, b.key === 'c:world');
    const sendBtn = form.querySelector('button:not(.loud-btn)') as HTMLButtonElement | null;
    if (sendBtn) sendBtn.disabled = b.disabled;
  }
  list.dataset.sel = b.key;
  patch(list, b.html);
  const fresh = feedKeys.get(list) !== b.key;
  feedKeys.set(list, b.key);
  const ls = lastShown as { ch: ChatChannel; ts: number } | null;
  if (ls && list.offsetParent !== null) markSeen(ls.ch, ls.ts);
  if (atBottom || fresh) list.scrollTop = list.scrollHeight;
}
/** World's chat box carries the Loudspeaker toggle; with it on the box takes a shorter line and Send reads Shout. */
function loudForm(form: HTMLFormElement, world: boolean) {
  let lb = form.querySelector('.loud-btn');
  if (!world) { lb?.remove(); form.classList.remove('loud'); }
  else {
    if (!lb) { form.insertAdjacentHTML('afterbegin', loudButton()); lb = form.querySelector('.loud-btn'); }
    lb!.classList.toggle('on', loudOn()); lb!.setAttribute('aria-pressed', String(loudOn()));
    form.classList.toggle('loud', loudOn());
  }
  const loud = world && loudOn();
  const inp = form.querySelector('input'), sendBtn = form.querySelector('button:not(.loud-btn)');
  if (inp) inp.maxLength = loud ? LOUD.maxLen : 200;
  if (sendBtn) sendBtn.textContent = loud ? 'Shout' : 'Send';
}
/** The Chat page's list after a page render. */
function stickFeed(list: HTMLElement | null) {
  if (!list) return;
  const k = list.dataset.sel ?? '';
  if (feedKeys.get(list) !== k) { feedKeys.set(list, k); list.scrollTop = list.scrollHeight; }
}

export function renderFeed() {
  const list = $('#feed');
  if (!list) return;
  drawChat(list, $('#chat') as HTMLFormElement, false);
  renderRailTabs();
  if (S.page === 'chat') render(true);
}

/** Send what was typed in a chat box: a message to the open conversation, a /w command, or a channel line. */
function chatSubmit(inp: HTMLInputElement) {
  const text = inp.value.trim();
  inp.value = '';
  if (!text) return;
  const sel = chatSel();
  const shout = loudCommand(text) ?? (sel.k === 'ch' && feedChannel() === 'world' && loudOn() ? text : null);
  if (shout) { send({ t: 'loud', text: shout }); setLoud(false); }
  else if (sel.k === 'dm') sendDm(sel.name, text);
  else if (!chatCommand(text)) { send({ t: 'chat', text, ch: feedChannel() }); if (S.page !== 'chat') setRailTab('chat'); }
  renderFeed();
}
function cpSubmit(form: HTMLFormElement): boolean {
  if (form.id !== 'cpform') return false;
  chatSubmit(form.querySelector('input') as HTMLInputElement);
  return true;
}

/** #/chat: the route picks the channel or conversation; #/chat alone keeps the last one. */
export function openChatRoute() {
  const r = chatRouteSel();
  if (r.dm) selectDm(r.dm);
  else if (r.list) selectList(r.list);
  else if (r.ch && isChannel(r.ch)) { selectChannel(); setRailTab(r.ch); }
  else loadInbox();
}

/** The Chat page: channels and conversations down the side, the chosen one beside them, the hunt running on. */
function chatPane() {
  const sel = chatSel(), ch = feedChannel();
  const r = route();
  const dmName = sel.k === 'dm' ? sel.name : '';
  const dm = dmWaiting();
  const chans = CHAT_CHANNELS.map((id) => {
    const on = sel.k === 'ch' && ch === id, n = on ? 0 : unread(id);
    return `<a class="cp-ch${on ? ' on' : ''}" href="#/chat/${id}" data-key="ch-${id}"><span>${id === 'guild' && S.trainer?.guild ? `${esc(channelName(id))} <span class="gtag">[${esc(S.trainer.guild.tag)}]</span>` : esc(channelName(id))}</span>${n ? `<span class="unread">${n > 99 ? '99+' : n}</span>` : ''}</a>`;
  }).join('');
  const title = sel.k === 'dm' ? convHeadHtml(sel.name, true)
    : sel.k === 'list' ? `<div class="cv-head"><a class="btn tiny cv-back" href="#/chat" aria-label="Back">‹</a><b>${sel.which === 'requests' ? 'Message requests' : 'Messages'}</b></div>`
    : `<div class="cv-head"><a class="btn tiny cv-back" href="#/chat" aria-label="Back">‹</a><b>${esc(channelName(ch))}</b>${ch === 'world' && S.playersOnline ? `<span class="muted">${S.playersOnline.toLocaleString()} online</span>` : ''}</div>`;
  const b = chatBody(true);
  const ls = lastShown as { ch: ChatChannel; ts: number } | null;
  if (ls) markSeen(ls.ch, ls.ts);
  return `<div class="chatpage${r.sub ? ' sub' : ''}">
    <aside class="panel cp-side">
      <h4>Channels</h4><nav class="cp-chans">${chans}</nav>
      <h4>Messages${dm.unread ? ` <span class="unread">${dm.unread}</span>` : ''}</h4>
      <div class="cp-convs">${listHtml(sel.k === 'list' && sel.which === 'requests' ? 'requests' : 'inbox', dmName, true)}</div>
      <label class="cp-from">Who can message you ${dmFromSelect()}</label>
    </aside>
    <section class="panel cp-main">${title}
      <div class="list feed cp-feed" id="cpfeed" data-sel="${esc(b.key)}">${b.html}</div>
      ${b.form ? `<form id="cpform" class="cp-form${b.key === 'c:world' && loudOn() ? ' loud' : ''}" autocomplete="off">${b.key === 'c:world' ? loudButton() : ''}<input maxlength="${b.key === 'c:world' && loudOn() ? LOUD.maxLen : 200}" placeholder="${esc(b.placeholder)}" aria-label="${esc(b.placeholder)}"${b.disabled ? ' disabled' : ''}><button class="btn primary"${b.disabled ? ' disabled' : ''}>${b.key === 'c:world' && loudOn() ? 'Shout' : 'Send'}</button></form>` : ''}
    </section></div>`;
}

/** A line's time; lines from before today (chat history) carry their day too. */
function lineTime(ts: number) {
  const d = new Date(ts), t = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${t}`;
}
function feedLine(b: Broadcast) {
  const card = !b.listing && b.creature && b.sid ? showCard(b) : '';
  const body = card || (b.kind === 'chat'
    ? `${tagChip(b.tag)}${b.outfit ? playerIcon({ outfit: b.outfit, comp: b.comp, size: 'sm' }) : ''}${chatBadge(b.badge)}<b>${b.who ? playerLink(b.who) : ''}:</b> ${b.listing ? listingCard(b) : esc(chatShown(b.text))}`
    : b.kind === 'record' ? recordLine(b) : esc(b.text));
  return `<div class="b-${b.kind}${b.loud ? ' b-loud' : ''}${b.listing ? ' b-listing' : ''}${card ? ' b-show' : ''}" data-key="${b.ts}-${esc(b.text.slice(0, 24))}"><span class="t">${lineTime(b.ts)}</span>${body}</div>`;
}

/**
 * A creature in World as an inline card: who, the creature, its grade and roll pills. Big catches post one by
 * themselves (kind aura, bloodline or grade); Show in chat posts one as a chat line. Hovering the sprite or name
 * (a long-press on a phone) opens the full hover card from the snapshot.
 */
function showCard(b: Broadcast) {
  const c = b.creature!, big = b.kind !== 'chat';
  const verb = big ? 'caught' : 'shows';
  return `<div class="show-card${big ? ` big k-${b.kind}` : ''}${c.rolls.aura ? ' aura' : ''}">
    <span class="sc-pic" data-showcase="${b.sid}" tabindex="0"><img src="${spriteUrl(creatureArt(c), c.rolls.aura)}" alt="${esc(SPECIES[c.species]?.name ?? '')}"></span>
    <div class="sc-main">
      <div class="sc-who">${b.outfit ? playerIcon({ outfit: b.outfit, comp: b.comp, size: 'sm' }) : ''}${tagChip(b.tag)}${b.who ? playerLink(b.who, `<b>${esc(b.who)}</b>`) : ''}<span class="sc-verb">${verb}</span>${b.record ? `<a class="sc-rec" href="#/records/${c.species}">Record</a>` : ''}</div>
      <div class="sc-name" data-showcase="${b.sid}"><b>${esc(cname(c))}</b><span class="muted">Lv ${full(c.level)}</span></div>
      ${miniPills(c)}
    </div></div>`;
}

/** A market listing shared in Trade: what, the price, and a button that opens the market at it. A creature opens its hover card. */
function listingCard(b: Broadcast) {
  const l = b.listing!, c = b.creature;
  const pic = c ? spriteUrl(c.species, c.rolls.aura) : l.icon ? iconUrl(l.icon) : '';
  return `<div class="lcard"${c && b.sid ? ` data-showcase="${b.sid}" tabindex="0"` : ''}>${pic ? `<img src="${pic}" alt="">` : ''}
    <div class="lc-main"><div class="lc-name"><b>${esc(c ? cname(c) : l.name)}</b>${c ? `${creatureGrade(c, 'sm')}${c.rolls.aura ? auraTag() : ''}<span class="muted">Lv ${full(c.level)}</span>` : ''}</div>
      <div class="lc-price"><img src="${curIcon(l.cur)}" alt="">${l.unit && l.qty > 1 ? `${full(l.unit)} ${curWord(l.cur)} each <span class="muted">· ${full(l.qty)} listed</span>` : `${full(l.price)} ${curWord(l.cur)}${l.qty > 1 ? ` <span class="muted">· ${full(l.price / l.qty)} each</span>` : ''}`}</div></div>
    <a class="btn tiny primary" href="#/market/${l.id}">View listing</a></div>`;
}

// ---------------- listing dialog ----------------
/**
 * A full team: bring creature `id` in, choosing who goes to storage. lead: it leads; otherwise it takes the place of
 * the one it replaces.
 */
async function swapIn(id: number, lead: boolean) {
  const t = S.trainer, c = S.creatures.get(id);
  if (!t || !c) return;
  const rows = t.team.map((x) => S.creatures.get(x)).filter((x): x is CreatureDTO => !!x).map((m) =>
    `<button class="swap-row" data-dlg="${m.id}">${creatureIcon(m.species, m.rolls.aura)}<b>${esc(cname(m))}</b><span class="muted">Lv ${full(m.level)}</span></button>`).join('');
  const r = await openDialog({ title: lead ? `Make ${cname(c)} lead` : `Swap in ${cname(c)}`, body: `<p class="muted">Who goes to storage?</p><div class="swap-pick">${rows}</div>`, actions: [] });
  const out = Number(r.value);
  const now = S.trainer;
  if (!out || !now || !now.team.includes(out) || now.team.includes(id)) return;
  const ids = lead ? [id, ...now.team.filter((x) => x !== out)] : now.team.map((x) => (x === out ? id : x));
  send({ t: 'team', ids });
}

function listCreature(id: number): Promise<boolean> {
  const c = S.creatures.get(id);
  if (!c) return Promise.resolve(false);
  const notes = [(S.trainer!.charmed ?? []).includes(id) ? 'Its XP Charm goes back to your bag.' : '', c.held ? `Its ${heldName(c.held)} comes off and stays with you.` : ''].filter(Boolean);
  return listDialog({ kind: 'creature', ref: String(id), name: cname(c), pkey: c.species, note: notes.join(' ') });
}

/** Price (per unit for items, and quantity) with the fees spelled out before anything is charged. */
async function listDialog(o: { kind: 'creature' | 'item' | 'relic' | 'shrine' | 'held'; ref: string; name: string; /** Price-table key: species, item id (the ref), relic stat or shrine tier. */ pkey?: string; maxQty?: number; note?: string; /** The currency picked first (gems only while they are open). */ cur?: MarketCurrency }): Promise<boolean> {
  const hold = CONFIG.marketHoldMs / 60_000, days = CONFIG.marketListingDays, cut = CONFIG.marketFeeCoins;
  const item = o.kind === 'item';
  // The seller picks the currency while gems are open (shared/gems.ts); otherwise everything is in coins.
  // Charms, and relics that change drop or catch odds (worn or not), list for coins only.
  const oddsOk = o.kind === 'item' ? gemListable(ITEM[o.ref]?.kind) : o.kind === 'relic' ? relicGemListable(o.pkey) : o.kind === 'creature' ? relicGemListable(S.creatures.get(Number(o.ref))?.relic?.stat) : true;
  const gemsOpen = !!S.market?.curs.includes('gems') && oddsOk;
  const lowLevel = (S.trainer?.level ?? 0) < GEM_LIST_LEVEL;
  const cr = o.kind === 'creature' ? S.creatures.get(Number(o.ref)) : undefined;
  const lowRating = !!cr && !creatureGemRatingOk(ratingOf(cr.rolls, cr.species), SPECIES[cr.species]?.rarity);
  const gemsLocked = gemsOpen && (lowLevel || lowRating);
  const lockWhy = lowLevel ? `Needs Lv ${GEM_LIST_LEVEL}` : `Needs rating ${GEM_MIN_RATING}+`;
  const first: MarketCurrency = gemsOpen && !gemsLocked && o.cur === 'gems' ? 'gems' : 'coins';
  // One listing per item kind and currency: more of an item already listed goes onto that listing at its price.
  const stackOf = (cur: MarketCurrency) => item ? S.market?.listings.find((l) => l.mine && l.kind === 'item' && l.ref === o.ref && l.unit > 0 && (l.cur ?? 'coins') === cur) : undefined;
  const minOf = (cur: MarketCurrency) => (cur === 'gems' ? GEM_MIN_UNIT : MARKET_MIN_UNIT);
  const stack = stackOf(first);
  const r = await openDialog({
    title: `List ${o.name} for sale`,
    body: `${gemsOpen ? `<div class="dlg-cur" role="radiogroup" aria-label="Currency">${(['coins', 'gems'] as const).map((c) => `<label><input type="radio" name="cur" value="${c}"${c === first ? ' checked' : ''}${c === 'gems' && gemsLocked ? ' disabled' : ''}><img src="${curIcon(c)}" alt="">${c === 'gems' ? `Gems${gemsLocked ? ` <span class="sub">${lockWhy}</span>` : ''}` : 'Coins'}</label>`).join('')}</div>` : ''}
      ${item ? `<label class="dlg-field"><span>Quantity</span><input name="qty" type="number" min="1" max="${o.maxQty ?? 1}" value="1" inputmode="numeric"> <span class="sub">of ${full(o.maxQty ?? 0)}</span></label>` : ''}
      <label class="dlg-field"><span>${item ? 'Price each' : 'Price'}</span><span class="coin-in"><img src="${curIcon(first)}" alt="" data-curimg><input name="price" type="number" min="${minOf(first)}" placeholder="${first === 'gems' ? 'Gems' : 'Coins'}" inputmode="numeric"${stack ? ` value="${stack.unit}" readonly` : ''}></span></label>
      <p class="sub" data-stack>${stack ? `Listed: ${full(stack.qty)} at ${full(stack.unit)} each` : ''}</p>
      <div data-pxwrap>${pxSlot(o.kind, item ? o.ref : o.pkey ?? '')}</div>
      <p class="fee mk-fee" data-fee></p>
      <p class="sub">${hold} min draw · lasts ${days} days · price fixed${item ? ' · sold per unit' : ''}</p>
      ${o.note ? `<p>${esc(o.note)}</p>` : ''}`,
    actions: [{ label: 'List for sale', value: 'list', kind: 'primary', disabled: true }],
    onInput: (root, d) => {
      const cur: MarketCurrency = d.cur === 'gems' ? 'gems' : 'coins', gems = cur === 'gems', min = minOf(cur), word = gems ? 'gems' : 'coins';
      // Switching currency swaps the price field's icon, the stack it adds to, and the coin price line.
      const img = root.querySelector<HTMLImageElement>('[data-curimg]')!, input = root.querySelector<HTMLInputElement>('input[name="price"]')!;
      if (img.dataset.cur !== cur) {
        img.dataset.cur = cur; img.src = curIcon(cur);
        input.placeholder = gems ? 'Gems' : 'Coins'; input.min = String(min);
        const st = stackOf(cur);
        const wasStack = input.readOnly;
        input.readOnly = !!st; if (st) input.value = String(st.unit); else if (wasStack) input.value = '';
        root.querySelector('[data-stack]')!.textContent = st ? `Listed: ${full(st.qty)} at ${full(st.unit)} each` : '';
        root.querySelector<HTMLElement>('[data-pxwrap]')!.hidden = gems;
      }
      const price = Math.floor(Number(input.value) || 0), qty = Math.floor(Number(d.qty ?? 1) || 0), total = price * qty;
      const fee = gems ? 0 : listingFee(total), cutNow = gems ? gemFee(o.kind, total) : marketFee(total);
      const ok = price >= min && qty >= 1 && qty <= (o.maxQty ?? 1) && S.trainer!.coins >= fee;
      const btn = [...root.querySelectorAll<HTMLButtonElement>('.actions button')].pop()!;
      btn.disabled = !ok;
      btn.textContent = price >= min ? (item ? `List ${full(qty)} at ${full(price)} ${word} each` : `List for ${full(price)} ${word}`) : 'List for sale';
      // The fees, spelled out as the price is typed: what is paid now, what the sale costs, what the seller keeps.
      root.querySelector('[data-fee]')!.innerHTML = price < min
        ? (gems ? `<span>No listing fee · ${gemFeePct(o.kind, 0)}%${o.kind === 'creature' ? ' or less' : ''} in gems when it sells · at least ${min} gems${item ? ' each' : ''}</span>` : `<span>Listing fee 1% now (min 10) · ${cut * 100}% when it sells · at least ${MARKET_MIN_UNIT} coins${item ? ' each' : ''}</span>`)
        : gems
          ? `<span>Market fee on sale, ${gemFeePct(o.kind, total)}% <b>−${full(cutNow)}</b></span><span class="fee-net">You receive${qty > 1 ? ' if all sell' : ''} <b>${full(total - cutNow)} gems</b> <i>cashes out ${cashoutUsd(total - cutNow)}</i></span>`
          : `<span>Listing fee, paid now <b>${full(fee)}</b></span><span>Market fee on sale, ${cut * 100}% <b>−${full(cutNow)}</b></span><span class="fee-net">You receive${qty > 1 ? ' if all sell' : ''} <b>${full(total - cutNow)}</b></span>${S.trainer!.coins < fee ? '<span class="fee-short">Not enough coins for the listing fee</span>' : ''}`;
    },
  });
  if (!r.value) return false;
  const cur: MarketCurrency = gemsOpen && r.data.cur === 'gems' ? 'gems' : 'coins';
  const price = Math.floor(Number(r.data.price)), qty = item ? Math.floor(Number(r.data.qty)) : 1;
  if (!(price >= minOf(cur) && qty >= 1)) return false;
  send({ t: 'list', kind: o.kind, ref: o.ref, qty, price, ...(cur === 'gems' ? { cur } : {}) });
  return true;
}
